import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '../../theme/colors';
import {
  getConfig,
  updateConfig,
  compileConfig,
  updatePublishedConfig,
  publishConfig,
  archiveConfig,
  deleteConfig,
  optimizePointRules,
  optimizerContextFromForm,
  getStats,
  drawLottery,
  aiRouteDraft,
  finalizeRoutes,
  requestRewardIssuance,
  formToRawInput,
  statusLabel,
  ACTION_OPTIONS,
  ACTION_VERIFICATION,
  BADGE_TYPE_OPTIONS,
  BADGE_REQ_OPTIONS,
  INVENTORY_POLICY_OPTIONS,
  REWARD_KIND_OPTIONS,
  EXPIRY_TYPE_OPTIONS,
  SPONSOR_TYPE_OPTIONS,
  GATHERING_TAG_KEYS,
  GATHERING_TAG_OPTIONS,
  BLANK_SPONSOR,
  BLANK_POINT_RULE,
  BLANK_BADGE,
  BLANK_REWARD,
  BLANK_SESSION,
  BLANK_LOTTERY_POOL,
  BLANK_TICKET_TIER,
  searchMerchants,
} from '../../lib/activityConfigsApi';
import { uploadImage, uploadMedia } from '../../lib/uploadApi';
import { usePortalT } from '../../i18n/usePortalT';
import GeoDesigner from '../../components/organizer/GeoDesigner';
import { QRCodeCanvas } from 'qrcode.react';
import {
  PageTitle,
  PageSubtitle,
  Btn,
  BtnDanger,
  BtnRow,
  Card,
  Field,
  Badge,
  ErrorText,
  StatGrid,
  StatCard,
  StatLabel,
  StatValue,
} from '../../styles/adminStyles';

// --- small layout helpers ---------------------------------------------------

const Section = styled(Card)`
  margin-bottom: 18px;
`;
const SectionTitle = styled.h2`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 900;
`;
const SectionHint = styled.p`
  margin: 0 0 14px;
  font-size: 12px;
  color: ${colors.inkMuted};
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || 'repeat(auto-fit, minmax(160px, 1fr))'};
  gap: 12px;
  align-items: end;
`;
const RepeatItem = styled.div`
  border: 1px dashed ${colors.border};
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
  background: ${colors.bg};
`;
const RemoveLink = styled.button`
  background: none;
  border: none;
  color: #b91c1c;
  font-weight: 800;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
`;
const PreviewPanel = styled(Card)`
  background: ${colors.accentSoft};
  border-color: ${colors.accent};
  margin-bottom: 18px;
`;
const Warn = styled.p`
  color: #b45309; font-size: 12px; font-weight: 700; margin: 4px 0;
`;
const SimResultBox = styled.div`
  font-size: 13px;
  background: #fff;
  border: 1px solid ${colors.border};
  border-radius: 10px;
  padding: 10px 12px;
  line-height: 1.5;
`;
const AnalyticsTable = styled.div`
  overflow-x: auto;
  margin-top: 10px;
  table {
    width: 100%;
    min-width: 680px;
    border-collapse: collapse;
    font-size: 12px;
  }
  th, td {
    padding: 9px 10px;
    border-bottom: 1px solid ${colors.border};
    text-align: left;
    white-space: nowrap;
  }
  th {
    color: ${colors.inkMuted};
    font-weight: 800;
    background: ${colors.bgAlt};
  }
  td:first-child { font-weight: 700; }
`;
const ChatBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
  padding: 4px 0;
  margin-bottom: 8px;
`;
const ChatBubble = styled.div`
  align-self: ${(p) => (p.$role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 85%;
  font-size: 13px;
  line-height: 1.5;
  padding: 8px 11px;
  border-radius: 12px;
  white-space: pre-wrap;
  background: ${(p) => (p.$role === 'user' ? colors.accent : '#fff')};
  color: ${(p) => (p.$role === 'user' ? '#fff' : colors.ink)};
  border: 1px solid ${(p) => (p.$role === 'user' ? colors.accent : colors.border)};
`;
const SuggestionBox = styled.div`
  background: ${colors.accentSoft};
  border: 1px solid ${colors.accent};
  border-radius: 10px;
  padding: 10px 12px;
  margin: 8px 0;
`;

const BadgeThumb = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  img { width: 40px; height: 40px; object-fit: contain; border: 1px solid ${colors.border}; border-radius: 8px; background: #fff; }
`;
const UploadBtn = styled.label`
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 8px 12px;
  border: 1px dashed ${colors.border};
  border-radius: 10px;
  cursor: pointer;
  color: ${colors.ink};
  background: ${colors.bg};
  input { display: none; }
`;

/** Badge artwork (PNG/JPG/WebP/SVG) upload -> stores a public GCS URL in `iconUrl`. */
function BadgeImageField({ value, onChange, disabled }) {
  const { t } = usePortalT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const inputRef = useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    setErr(null);
    setBusy(true);
    const res = await uploadImage(file, { kind: 'badge' });
    setBusy(false);
    if (res.success) {
      onChange(res.url);
    } else {
      const map = {
        file_too_large: t('activities.uploadTooLarge'),
        unsupported_content_type: t('activities.uploadBadType'),
        storage_not_configured: t('activities.storageOff'),
        storage_disabled: t('activities.storageOff'),
      };
      setErr(map[res.error] || t('activities.uploadFailed'));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {value ? (
        <BadgeThumb>
          <img src={value} alt="badge" />
          {!disabled ? (
            <RemoveLink type="button" onClick={() => onChange('')}>{t('activities.removeImage')}</RemoveLink>
          ) : null}
        </BadgeThumb>
      ) : null}
      {!disabled ? (
        <UploadBtn>
          {busy ? t('activities.uploading') : (value ? t('activities.uploadImage') : `＋ ${t('activities.uploadImage')}`)}
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" disabled={busy} onChange={onPick} />
        </UploadBtn>
      ) : null}
      {err ? <span style={{ color: '#b91c1c', fontSize: 12 }}>{err}</span> : null}
    </div>
  );
}

const MediaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;
const MediaTile = styled.div`
  position: relative;
  width: 112px;
  height: 112px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${(p) => (p.$over ? colors.accent : colors.border)};
  background: ${colors.bg};
  cursor: ${(p) => (p.$disabled ? 'default' : 'grab')};
  opacity: ${(p) => (p.$dragging ? 0.45 : 1)};
  box-shadow: ${(p) => (p.$over ? `0 0 0 2px ${colors.accent}33` : 'none')};
  transition: opacity 0.15s, border-color 0.15s, box-shadow 0.15s;
  &:active { cursor: ${(p) => (p.$disabled ? 'default' : 'grabbing')}; }
  img, video { width: 100%; height: 100%; object-fit: cover; display: block; pointer-events: none; user-select: none; }
`;
const MediaBadge = styled.span`
  position: absolute;
  left: 6px;
  top: 6px;
  font-size: 10px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(0,0,0,0.55);
  color: #fff;
`;
const MediaRemove = styled.button`
  position: absolute;
  right: 4px;
  top: 4px;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 999px;
  background: rgba(0,0,0,0.6);
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
`;

/** Activity cover + gallery (images/videos) — synced to the app event on publish. */
function ActivityMediaField({ items, onChange, disabled }) {
  const { t } = usePortalT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const inputRef = useRef(null);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = '';
    if (!files.length) return;
    setErr(null);
    setBusy(true);
    const added = [];
    for (const file of files) {
      const res = await uploadMedia(file, { kind: 'event' });
      if (res.success && res.url) {
        added.push({ url: res.url, type: res.type || 'image' });
      } else {
        const map = {
          file_too_large: t('activities.uploadTooLarge'),
          unsupported_content_type: t('activities.uploadMediaBadType'),
          storage_not_configured: t('activities.storageOff'),
          storage_disabled: t('activities.storageOff'),
        };
        setErr(map[res.error] || t('activities.uploadFailed'));
        break;
      }
    }
    setBusy(false);
    if (added.length) onChange([...(items || []), ...added]);
  };

  const removeAt = (idx) => onChange((items || []).filter((_, i) => i !== idx));

  const reorder = (from, to) => {
    if (from == null || to == null || from === to) return;
    const list = [...(items || [])];
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    onChange(list);
  };

  const onDragStart = (e, i) => {
    if (disabled) return;
    setDragIdx(i);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(i));
  };

  const onDragOver = (e, i) => {
    if (disabled || dragIdx == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIdx(i);
  };

  const onDrop = (e, i) => {
    if (disabled) return;
    e.preventDefault();
    const from = dragIdx ?? Number(e.dataTransfer.getData('text/plain'));
    reorder(from, i);
    setDragIdx(null);
    setOverIdx(null);
  };

  const onDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 700 }}>
        {t('activities.mediaLabel')}
        <span style={{ fontWeight: 400, color: colors.inkMuted }}>{t('activities.mediaHint')}</span>
      </div>
      {(items || []).length ? (
        <MediaGrid>
          {(items || []).map((m, i) => (
            <MediaTile
              key={`${m.url}-${i}`}
              draggable={!disabled}
              $disabled={disabled}
              $dragging={dragIdx === i}
              $over={overIdx === i && dragIdx !== i}
              onDragStart={(e) => onDragStart(e, i)}
              onDragOver={(e) => onDragOver(e, i)}
              onDrop={(e) => onDrop(e, i)}
              onDragEnd={onDragEnd}
              onDragLeave={() => { if (overIdx === i) setOverIdx(null); }}
            >
              {m.type === 'video' ? (
                <video src={m.url} muted playsInline crossOrigin="anonymous" />
              ) : (
                <img src={m.url} alt="" crossOrigin="anonymous" />
              )}
              <MediaBadge>{i === 0 ? t('activities.mediaCover') : (m.type === 'video' ? t('activities.mediaVideo') : t('activities.mediaImage'))}</MediaBadge>
              {!disabled ? (
                <MediaRemove type="button" aria-label="remove" onClick={() => removeAt(i)} onMouseDown={(e) => e.stopPropagation()}>×</MediaRemove>
              ) : null}
            </MediaTile>
          ))}
        </MediaGrid>
      ) : null}
      {!disabled ? (
        <UploadBtn>
          {busy ? t('activities.uploading') : `＋ ${t('activities.uploadMedia')}`}
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,video/mp4,video/quicktime"
            multiple
            disabled={busy}
            onChange={onPick}
          />
        </UploadBtn>
      ) : null}
      {err ? <span style={{ color: '#b91c1c', fontSize: 12 }}>{err}</span> : null}
    </div>
  );
}

/**
 * Single image/video slot for one route node. The uploaded media is shown as
 * the stage's transition background when the route is played in the app.
 */
function NodeMediaField({ value, onChange, disabled }) {
  const { t } = usePortalT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const inputRef = useRef(null);

  const onPick = async (e) => {
    const file = (e.target.files || [])[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    setErr(null);
    setBusy(true);
    const res = await uploadMedia(file, { kind: 'event' });
    setBusy(false);
    if (res.success && res.url) {
      onChange({ url: res.url, type: res.type === 'video' ? 'video' : 'image' });
    } else {
      const map = {
        file_too_large: t('activities.uploadTooLarge'),
        unsupported_content_type: t('activities.uploadMediaBadType'),
        storage_not_configured: t('activities.storageOff'),
        storage_disabled: t('activities.storageOff'),
      };
      setErr(map[res.error] || t('activities.uploadFailed'));
    }
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
        {t('activities.rwNodeMedia')}
        <span style={{ fontWeight: 400, color: colors.inkMuted }}>{t('activities.rwNodeMediaHint')}</span>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {value && value.url ? (
          <MediaTile style={{ width: 88, height: 88 }} $disabled>
            {value.type === 'video' ? (
              <video src={value.url} muted playsInline crossOrigin="anonymous" />
            ) : (
              <img src={value.url} alt="" crossOrigin="anonymous" />
            )}
            {!disabled ? (
              <MediaRemove type="button" aria-label="remove" onClick={() => onChange(null)}>×</MediaRemove>
            ) : null}
          </MediaTile>
        ) : null}
        {!disabled ? (
          <UploadBtn>
            {busy ? t('activities.uploading') : `＋ ${t('activities.rwNodeMediaAdd')}`}
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,video/mp4,video/quicktime"
              disabled={busy}
              onChange={onPick}
            />
          </UploadBtn>
        ) : null}
      </div>
      {err ? <span style={{ color: '#b91c1c', fontSize: 12 }}>{err}</span> : null}
    </div>
  );
}

const AutoWrap = styled.div`
  position: relative;
`;
const Suggestions = styled.ul`
  position: absolute;
  z-index: 30;
  top: 100%;
  left: 0;
  right: 0;
  margin: 4px 0 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid ${colors.border};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  max-height: 240px;
  overflow-y: auto;
`;
const SuggestItem = styled.li`
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  &:hover { background: ${colors.accentSoft}; }
`;
const LinkedChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #166534;
  background: #dcfce7;
  border-radius: 999px;
  padding: 3px 10px;
  margin-top: 4px;
`;
const SponsorRewards = styled.div`
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed ${colors.border};
  .hd { font-size: 12px; font-weight: 800; color: ${colors.ink}; margin-bottom: 6px; }
  .empty { font-size: 12px; color: ${colors.inkMuted}; }
  ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
  li { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 13px; }
  .name { font-weight: 700; }
  .meta { color: ${colors.inkMuted}; font-size: 12px; }
`;
const IssueBadge = styled.span`
  font-size: 11px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 999px;
  color: ${(p) => (p.$rejected ? '#991b1b' : p.$issued ? '#166534' : p.$muted ? '#64748b' : '#b45309')};
  background: ${(p) => (p.$rejected ? '#fee2e2' : p.$issued ? '#dcfce7' : p.$muted ? '#f1f5f9' : '#fef3c7')};
`;
const IssueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 8px;
  .note { font-size: 12px; color: ${colors.inkMuted}; }
`;
const RestoreBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 4px 0 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  color: #92400e;
  font-size: 13px;
  font-weight: 700;
  button {
    border: 1px solid #92400e;
    background: transparent;
    color: #92400e;
    font-size: 12px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 999px;
    cursor: pointer;
    &:hover { background: rgba(146, 64, 14, 0.08); }
  }
`;
const RefreshBtn = styled.button`
  border: 1px solid ${colors.border};
  background: #fff;
  color: ${colors.inkMuted};
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  line-height: 1.4;
  &:hover { background: #f8fafc; }
  &:disabled { opacity: 0.5; cursor: default; }
  .spin { display: inline-block; animation: issueSpin 0.8s linear infinite; }
  @keyframes issueSpin { to { transform: rotate(360deg); } }
`;

/**
 * Merchant name field with live search. Picking a suggestion auto-fills the
 * linked OnMyWay User ID (merchantUserId) — the foreign key used to route
 * sponsor coupons. Free typing keeps the name but clears any stale link.
 */
function MerchantNameField({ name, userId, onPick, onNameChange, disabled }) {
  const { t } = usePortalT();
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const blurRef = useRef(null);

  const runSearch = useCallback((q) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const query = String(q || '').trim();
      if (query.length < 1) { setResults([]); setLoading(false); return; }
      setLoading(true);
      const rows = await searchMerchants(query).catch(() => []);
      setResults(rows);
      setLoading(false);
      setOpen(true);
    }, 250);
  }, []);

  const handleType = (v) => {
    onNameChange(v);
    runSearch(v);
  };

  const pick = (m) => {
    onPick({ merchantName: m.brandName || m.name, merchantUserId: m.userId });
    setOpen(false);
    setResults([]);
  };

  return (
    <AutoWrap
      onBlur={() => { blurRef.current = setTimeout(() => setOpen(false), 150); }}
      onFocus={() => { if (blurRef.current) clearTimeout(blurRef.current); }}
    >
      <Field style={{ marginBottom: 0 }}>
        {t('activities.fMerchantName')}
        <input
          value={name}
          disabled={disabled}
          onChange={(e) => handleType(e.target.value)}
          onFocus={() => { if (results.length) setOpen(true); }}
          placeholder={t('activities.merchantSearchPh')}
          autoComplete="off"
        />
      </Field>
      {userId ? (
        <LinkedChip>
          ✓ {t('activities.merchantLinked')}: {userId}
          {!disabled ? (
            <RemoveLink type="button" style={{ color: '#166534' }} onClick={() => onPick({ merchantName: name, merchantUserId: '' })}>
              {t('activities.merchantUnlink')}
            </RemoveLink>
          ) : null}
        </LinkedChip>
      ) : (
        <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>{t('activities.merchantUnlinked')}</div>
      )}
      {open && !disabled ? (
        <Suggestions>
          {loading ? (
            <SuggestItem as="li" style={{ cursor: 'default', color: colors.inkMuted }}>{t('common.loading')}</SuggestItem>
          ) : results.length === 0 ? (
            <SuggestItem as="li" style={{ cursor: 'default', color: colors.inkMuted }}>{t('activities.merchantNoResults')}</SuggestItem>
          ) : (
            results.map((m) => (
              <SuggestItem key={m.userId} onMouseDown={() => pick(m)}>
                <span style={{ fontWeight: 800 }}>{m.brandName || m.name}{m.isOfficial ? ' ✓' : ''}</span>
                <span style={{ color: colors.inkMuted }}>
                  {m.username ? `@${m.username} · ` : ''}{m.userId}{m.category ? ` · ${m.category}` : ''}
                </span>
              </SuggestItem>
            ))
          )}
        </Suggestions>
      ) : null}
    </AutoWrap>
  );
}

// Badge creation/settings are hidden for now (per product decision). Flip to
// true to restore the Badges section (04) and badge-based reward redemption.
const SHOW_BADGES = false;

// Route creation preferences — mirror the app's「灵感描述」step (风格/节点类型).
// Labels carry zh/en so we can render them without a per-string i18n key.
const ROUTE_STYLE_OPTIONS = [
  { key: 'cultural_art', zh: '文化艺术线', en: 'Culture & Arts', emoji: '🎨' },
  { key: 'limited_time', zh: '短期限定线', en: 'Limited-Time', emoji: '✨' },
  { key: 'classic_checkin', zh: '传统打卡线', en: 'Classic Check-in', emoji: '📸' },
  { key: 'sunset_walk', zh: '散步线', en: 'Stroll', emoji: '🚶' },
  { key: 'food_must_try', zh: '必尝线', en: 'Must-Try Food', emoji: '🍜' },
  { key: 'script_adventure', zh: '剧本探险线', en: 'Script Adventure', emoji: '🎭' },
];
const ROUTE_NODE_TYPE_OPTIONS = [
  { value: 'observation', zh: '观察', en: 'Observation', emoji: '👁' },
  { value: 'logic', zh: '逻辑', en: 'Logic', emoji: '🧩' },
  { value: 'history', zh: '知识', en: 'Knowledge', emoji: '📚' },
  { value: 'code', zh: '密码', en: 'Code', emoji: '🔐' },
];
const DEFAULT_NODE_TYPES = ROUTE_NODE_TYPE_OPTIONS.map((o) => o.value);

const BLANK_FORM = {
  activity: { name: '', description: '', startDate: '', endDate: '', redemptionDeadline: '', location: '', locationLat: null, locationLng: null, mainVenueLocationId: null, locations: [], routes: [], sessions: [], mediaUrls: [], imageUrl: '', activityType: 'outdoor', targetParticipants: '' },
  sponsors: [],
  pointRules: [{ ...BLANK_POINT_RULE }],
  badges: [],
  lottery: { enabled: false, entryRequirementPoints: '', pointsPerTicket: '', maxEntriesPerUser: '', lotteryDate: '', winnerSelection: 'random', ticketTiers: [], ticketDrops: { enabled: false, totalTickets: '', maxPerUser: '', startAt: '', endAt: '', dropRate: '' }, pools: [] },
  rewards: [],
};

const centsToDollars = (c) => (c == null ? '' : String(c / 100));

/** Rebuild the editable form from a stored DSL-input rawInput. */
function rawInputToForm(raw) {
  if (!raw || !raw.activity) return { ...BLANK_FORM };
  const badges = (raw.badges || []).map((b) => ({
    name: b.name || '', icon: b.icon || '', iconUrl: b.iconUrl || '', type: b.type || 'progression', level: b.level ?? '',
    requirementType: b.requirement?.type || 'routes_completed',
    requirementCount: b.requirement?.count ?? '', routeId: b.requirement?.routeId || '', locationId: b.requirement?.locationId || '',
    points: b.points ?? 0,
  }));
  const keyToName = new Map();
  (raw.badges || []).forEach((b) => keyToName.set(b.key, b.name));
  return {
    activity: {
      name: raw.activity.name || '', description: raw.activity.description || '', startDate: raw.activity.startDate || '',
      endDate: raw.activity.endDate || '', redemptionDeadline: raw.activity.redemptionDeadline || '', location: raw.activity.location || '',
      locationLat: raw.activity.locationLat ?? null, locationLng: raw.activity.locationLng ?? null,
      mainVenueLocationId: raw.activity.mainVenueLocationId || null,
      locations: Array.isArray(raw.activity.locations) && raw.activity.locations.length
        ? raw.activity.locations.map((p, i) => ({ id: p.id || `loc_${i + 1}`, name: p.name || '', lat: p.lat, lng: p.lng, isMainVenue: p.isMainVenue === true }))
        : (Number.isFinite(raw.activity.locationLat) && Number.isFinite(raw.activity.locationLng)
            ? [{ id: 'loc_1', name: raw.activity.location || '', lat: raw.activity.locationLat, lng: raw.activity.locationLng, isMainVenue: true }]
            : []),
      routes: Array.isArray(raw.activity.routes)
        ? raw.activity.routes.map((r, i) => ({
            id: r.id || `route_${i + 1}`, name: r.name || '',
            title: r.title || '', synopsis: r.synopsis || '',
            theme: r.theme || '', routeStyle: r.routeStyle || '', inspiration: r.inspiration || '',
            preferredNodeTypes: Array.isArray(r.preferredNodeTypes) ? r.preferredNodeTypes : [],
            limitedTimeDeadline: r.limitedTimeDeadline || '',
            waypoints: (r.waypoints || []).map((w) => ({
              name: w.name || '', lat: w.lat, lng: w.lng,
              isPhysical: w.isPhysical === true,
              media: w.media && w.media.url ? { url: w.media.url, type: w.media.type === 'video' ? 'video' : 'image' } : null,
              riddle: w.riddle ? { question: w.riddle.question || '', answer: w.riddle.answer || '', hint: w.riddle.hint || '', clueType: w.riddle.clueType || null } : { question: '', answer: '', hint: '', clueType: null },
            })),
          }))
        : [],
      sessions: Array.isArray(raw.activity.sessions)
        ? raw.activity.sessions.map((s, i) => ({
            id: s.id || `session_${i + 1}`, name: s.name || '', date: s.date || '', startTime: s.startTime || '',
            endTime: s.endTime || '', locationRef: s.locationRef || '', routeRef: s.routeRef || '', description: s.description || '',
          }))
        : [],
      mediaUrls: Array.isArray(raw.activity.mediaUrls)
        ? raw.activity.mediaUrls.map((m) => ({
            url: m.url || '',
            type: m.type === 'video' ? 'video' : 'image',
          })).filter((m) => m.url)
        : [],
      imageUrl: raw.activity.imageUrl || '',
      activityType: GATHERING_TAG_KEYS.includes(raw.activity.activityType) ? raw.activity.activityType : (raw.activity.activityType || 'outdoor'),
      targetParticipants: raw.activity.targetParticipants ?? '',
    },
    sponsors: (raw.sponsors || []).map((s) => ({
      merchantName: s.merchantName || '', merchantUserId: s.merchantUserId || '', sponsorType: s.sponsorType || 'supporting',
      contribution: s.contribution || '', rewardBudgetDollars: centsToDollars(s.rewardBudgetCents), rewardRefId: s.rewardRefId || '',
    })),
    pointRules: (raw.pointRules || []).map((r) => ({
      action: r.action || 'route_complete', description: r.description || '', points: r.points ?? 0,
      dailyLimit: r.dailyLimit ?? '', totalLimit: r.totalLimit ?? '', verification: r.verification || 'gps',
      sessionRef: (r.metadata && r.metadata.sessionRef) || '',
    })),
    badges,
    lottery: {
      enabled: !!raw.lottery?.enabled, entryRequirementPoints: raw.lottery?.entryRequirementPoints ?? '', pointsPerTicket: raw.lottery?.pointsPerTicket ?? '',
      maxEntriesPerUser: raw.lottery?.maxEntriesPerUser ?? '', lotteryDate: raw.lottery?.lotteryDate || '',
      winnerSelection: raw.lottery?.winnerSelection || 'random',
      ticketTiers: Array.isArray(raw.lottery?.ticketTiers)
        ? raw.lottery.ticketTiers.map((t) => ({ points: t.points ?? '', tickets: t.tickets ?? '' }))
        : [],
      ticketDrops: {
        enabled: !!raw.lottery?.ticketDrops?.enabled,
        totalTickets: raw.lottery?.ticketDrops?.totalTickets ?? '',
        maxPerUser: raw.lottery?.ticketDrops?.maxPerUser ?? '',
        startAt: raw.lottery?.ticketDrops?.startAt || '',
        endAt: raw.lottery?.ticketDrops?.endAt || '',
        dropRate: raw.lottery?.ticketDrops?.dropRate ?? '',
      },
      pools: Array.isArray(raw.lottery?.pools) && raw.lottery.pools.length > 0
        ? raw.lottery.pools.map((p) => ({ key: p.key || '', name: p.name || '', prize: p.prize || '', winnerCount: p.winnerCount ?? '', sponsorRef: p.sponsorRef || '' }))
        // Back-compat: lift a legacy single prize into one pool row.
        : ((raw.lottery?.prize || raw.lottery?.winnerCount != null)
            ? [{ key: '', name: '', prize: raw.lottery?.prize || '', winnerCount: raw.lottery?.winnerCount ?? '', sponsorRef: raw.lottery?.sponsorRef || '' }]
            : []),
    },
    rewards: (raw.rewards || []).map((rw) => ({
      title: rw.title || '', description: rw.description || '', mode: rw.mode || 'points_only', costPoints: rw.costPoints ?? '',
      requiredBadge: rw.requiredBadge ? (keyToName.get(rw.requiredBadge) || rw.requiredBadge) : '', sponsorRef: rw.sponsorRef || '', kind: rw.kind || 'coupon',
      valueDollars: centsToDollars(rw.valueCents), inventory: rw.inventory ?? '', inventoryPolicy: rw.inventoryPolicy || 'limited',
      perUserLimit: rw.perUserLimit ?? '', totalBudgetDollars: centsToDollars(rw.totalBudgetCents),
      expiryType: rw.expiry?.type || 'event_date', expiryValue: rw.expiry?.value ?? '',
    })),
  };
}

/**
 * Participation QR payload (mirrors lib/qrCode.ts on the app side):
 *   - join the activity          → onmyway://activity/{configId}
 *   - check in to a mini-activity → onmyway://activity/{configId}/s/{sessionId}
 */
function buildActivityQrPayload(configId, sessionId) {
  const base = `onmyway://activity/${encodeURIComponent(String(configId || '').trim())}`;
  return sessionId ? `${base}/s/${encodeURIComponent(String(sessionId).trim())}` : base;
}

/** A single printable QR poster tile (activity join or a timed mini-activity). */
function QrPoster({ title, subtitle, payload, downloadLabel }) {
  const ref = React.useRef(null);
  const download = () => {
    const canvas = ref.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${String(title || 'activity').replace(/[^\w\u4e00-\u9fa5-]+/g, '_')}_qr.png`;
    a.click();
  };
  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 10, padding: 12, textAlign: 'center', background: '#fff' }}>
      <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 2, color: colors.ink }}>{title}</div>
      {subtitle ? <div style={{ fontSize: 11, color: colors.inkMuted, marginBottom: 8 }}>{subtitle}</div> : null}
      <div ref={ref} style={{ display: 'inline-block' }}>
        <QRCodeCanvas value={payload} size={148} includeMargin level="M" />
      </div>
      <div>
        <button
          type="button"
          onClick={download}
          style={{ marginTop: 8, fontSize: 12, border: `1px solid ${colors.border}`, background: colors.bgAlt, color: colors.ink, borderRadius: 999, padding: '4px 12px', cursor: 'pointer' }}
        >
          {downloadLabel}
        </button>
      </div>
    </div>
  );
}

export default function ActivityEditorPage({ basePath = '/organizer' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, tEnum, language } = usePortalT();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [stats, setStats] = useState(null);
  const [editingPublished, setEditingPublished] = useState(false);
  // Simulator: expected number of participants who will complete each point rule
  // (keyed by rule index). Drives the aggregate reward-economy readout.
  const [simCounts, setSimCounts] = useState({});
  // AI point-rule optimizer: chat transcript, input box, in-flight flag and the
  // latest suggested rule set (one-click apply).
  const [optimizeChat, setOptimizeChat] = useState([]);
  const [optimizeInput, setOptimizeInput] = useState('');
  const [optimizeBusy, setOptimizeBusy] = useState(false);
  const [optimizeSuggestion, setOptimizeSuggestion] = useState(null);
  // Route AI co-creation: which route index is generating, + per-route chat text.
  const [routeAiBusy, setRouteAiBusy] = useState(null);
  const [routeAiFeedback, setRouteAiFeedback] = useState({});
  // Sponsor issuance state: reward keys aligned to the form order + a map of
  // reward key -> issuance ({ status, voucherId, merchantBrandName }).
  const [rewardKeys, setRewardKeys] = useState([]);
  const [issuances, setIssuances] = useState({});
  const [issRefreshing, setIssRefreshing] = useState(false);

  // --- Local draft cache ----------------------------------------------------
  // Persist the in-progress form to localStorage so an accidental refresh /
  // back-navigation never loses the user's typed-in data. Keyed per config id.
  // `hydratedRef` gates autosave so we never overwrite a real cached draft with
  // the initial blank/server state before restore has happened.
  const cacheKey = id ? `omw:activityEditorDraft:${id}` : null;
  const hydratedRef = useRef(false);
  const [restoredFromCache, setRestoredFromCache] = useState(false);
  const clearDraftCache = useCallback(() => {
    setRestoredFromCache(false);
    try { if (cacheKey) window.localStorage.removeItem(cacheKey); } catch { /* ignore */ }
  }, [cacheKey]);

  const isNL = config?.sourceType === 'natural_language';
  const livePublished = config?.status === 'published';
  // While editing a live activity, reuse the existing unlocked form rendering;
  // the server keeps the persisted activity published until Update succeeds.
  const published = livePublished && !editingPublished;
  const compiled = config?.status === 'compiled' || livePublished;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    hydratedRef.current = false; // pause autosave while we (re)hydrate
    const out = await getConfig(id);
    if (out.success && out.config) {
      setConfig(out.config);
      const serverForm = rawInputToForm(out.config.rawInput);
      const serverPrompt = out.config.rawInput?.prompt || '';
      setForm(serverForm);
      if (serverPrompt) setPrompt(serverPrompt);
      setRewardKeys(out.config.rewardKeys || []);
      setIssuances(out.config.rewardIssuances || {});

      // Restore locally-cached unsaved edits (accidental refresh / back). We
      // keep a cache only when it actually differs from the saved server copy;
      // a matching cache is dropped so we never show a spurious "restored"
      // banner. Persist actions clear the cache, so a surviving cache always
      // represents genuine unsaved work.
      let restored = false;
      try {
        const rawCache = cacheKey ? window.localStorage.getItem(cacheKey) : null;
        if (rawCache) {
          const parsed = JSON.parse(rawCache);
          const cachedForm = parsed?.form;
          const cachedPrompt = typeof parsed?.prompt === 'string' ? parsed.prompt : '';
          const formDiffers = cachedForm && JSON.stringify(cachedForm) !== JSON.stringify(serverForm);
          const promptDiffers = cachedPrompt !== serverPrompt;
          if (formDiffers || promptDiffers) {
            if (cachedForm) setForm(cachedForm);
            if (typeof parsed?.prompt === 'string') setPrompt(parsed.prompt);
            restored = true;
          } else if (cacheKey) {
            window.localStorage.removeItem(cacheKey);
          }
        }
      } catch { /* ignore malformed cache */ }
      setRestoredFromCache(restored);

      if (out.config.status === 'published') {
        getStats(id).then((s) => s.success && setStats(s.stats));
      }
    } else {
      setError(out.error || t('activities.notFound'));
    }
    setLoading(false);
    hydratedRef.current = true; // resume autosave now that state is settled
  }, [id, t, cacheKey]);

  useEffect(() => { load(); }, [load]);

  // --- form mutation helpers -----------------------------------------------
  const setActivity = (field, val) => setForm((f) => ({ ...f, activity: { ...f.activity, [field]: val } }));
  const setLottery = (field, val) => setForm((f) => ({ ...f, lottery: { ...f.lottery, [field]: val } }));
  const setRow = (list, idx, field, val) => setForm((f) => ({ ...f, [list]: f[list].map((r, i) => (i === idx ? { ...r, [field]: val } : r)) }));

  // Changing a point-rule's action re-derives its (fixed) verification method and
  // clears the session binding unless it's a timed mini-activity check-in.
  const setPointRuleAction = (idx, action) => setForm((f) => ({
    ...f,
    pointRules: f.pointRules.map((r, i) => (i === idx
      ? { ...r, action, verification: ACTION_VERIFICATION[action] || r.verification || 'gps', sessionRef: action === 'session_checkin' ? r.sessionRef : '' }
      : r)),
  }));
  const addRow = (list, blank) => setForm((f) => ({ ...f, [list]: [...f[list], { ...blank }] }));
  const removeRow = (list, idx) => setForm((f) => ({ ...f, [list]: f[list].filter((_, i) => i !== idx) }));
  // Sub-activities ("sessions") live nested under form.activity.sessions.
  const setSession = (idx, field, val) => setForm((f) => ({ ...f, activity: { ...f.activity, sessions: (f.activity.sessions || []).map((s, i) => (i === idx ? { ...s, [field]: val } : s)) } }));
  const addSession = () => setForm((f) => ({ ...f, activity: { ...f.activity, sessions: [...(f.activity.sessions || []), { ...BLANK_SESSION, id: `session_${Date.now()}` }] } }));
  const removeSession = (idx) => setForm((f) => ({ ...f, activity: { ...f.activity, sessions: (f.activity.sessions || []).filter((_, i) => i !== idx) } }));
  // --- Route co-creation (web AI console) ----------------------------------
  // Routes are drawn in the GeoDesigner (section 01); their per-node content
  // (riddle + physical/virtual) is authored here.
  const setRouteField = (idx, field, val) => setForm((f) => ({ ...f, activity: { ...f.activity, routes: (f.activity.routes || []).map((r, i) => (i === idx ? { ...r, [field]: val } : r)) } }));
  const setRouteNode = (routeIdx, nodeIdx, patch) => setForm((f) => ({ ...f, activity: { ...f.activity, routes: (f.activity.routes || []).map((r, i) => (i !== routeIdx ? r : { ...r, waypoints: (r.waypoints || []).map((w, j) => (j !== nodeIdx ? w : { ...w, ...patch })) })) } }));
  const setRouteNodeRiddle = (routeIdx, nodeIdx, field, val) => setForm((f) => ({ ...f, activity: { ...f.activity, routes: (f.activity.routes || []).map((r, i) => (i !== routeIdx ? r : { ...r, waypoints: (r.waypoints || []).map((w, j) => (j !== nodeIdx ? w : { ...w, riddle: { question: '', answer: '', hint: '', clueType: null, ...(w.riddle || {}), [field]: val } })) })) } }));
  // Toggle a preferred node type on a route (mirrors app: keep at least one).
  const toggleRouteNodeType = (idx, value) => setForm((f) => ({ ...f, activity: { ...f.activity, routes: (f.activity.routes || []).map((r, i) => {
    if (i !== idx) return r;
    const cur = (r.preferredNodeTypes && r.preferredNodeTypes.length) ? r.preferredNodeTypes : DEFAULT_NODE_TYPES;
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    return { ...r, preferredNodeTypes: next.length ? next : cur };
  }) } }));

  // Lottery prize pools (奖池) live nested under form.lottery.pools.
  const setLotteryPool = (idx, field, val) => setForm((f) => ({ ...f, lottery: { ...f.lottery, pools: (f.lottery.pools || []).map((p, i) => (i === idx ? { ...p, [field]: val } : p)) } }));
  const addLotteryPool = () => setForm((f) => ({ ...f, lottery: { ...f.lottery, pools: [...(f.lottery.pools || []), { ...BLANK_LOTTERY_POOL }] } }));
  const removeLotteryPool = (idx) => setForm((f) => ({ ...f, lottery: { ...f.lottery, pools: (f.lottery.pools || []).filter((_, i) => i !== idx) } }));

  // Customizable "N points → M tickets" exchange tiers (form.lottery.ticketTiers).
  const setTicketTier = (idx, field, val) => setForm((f) => ({ ...f, lottery: { ...f.lottery, ticketTiers: (f.lottery.ticketTiers || []).map((t, i) => (i === idx ? { ...t, [field]: val } : t)) } }));
  const addTicketTier = () => setForm((f) => ({ ...f, lottery: { ...f.lottery, ticketTiers: [...(f.lottery.ticketTiers || []), { ...BLANK_TICKET_TIER }] } }));
  const removeTicketTier = (idx) => setForm((f) => ({ ...f, lottery: { ...f.lottery, ticketTiers: (f.lottery.ticketTiers || []).filter((_, i) => i !== idx) } }));
  // Random ticket-drop config (form.lottery.ticketDrops).
  const setTicketDrop = (field, val) => setForm((f) => ({ ...f, lottery: { ...f.lottery, ticketDrops: { ...(f.lottery.ticketDrops || {}), [field]: val } } }));

  // Sponsors that carry a linked merchantUserId can receive reward assignments
  // (the reward's sponsorRef must equal the merchant's User ID to resolve).
  const sponsorOptions = useMemo(
    () => form.sponsors.filter((s) => s.merchantUserId).map((s) => ({ value: s.merchantUserId, label: s.merchantName || s.merchantUserId })),
    [form.sponsors],
  );
  // Pins/routes drawn in the GeoDesigner become selectable anchors for sub-activities.
  const locationOptions = useMemo(
    () => (form.activity.locations || []).map((p, i) => ({ value: p.id || `loc_${i + 1}`, label: p.name || `${t('activities.geoPoint')} ${i + 1}` })),
    [form.activity.locations, t],
  );
  const routeOptions = useMemo(
    () => (form.activity.routes || []).map((r, i) => ({ value: r.id || `route_${i + 1}`, label: r.name || `${t('activities.geoRoute')} ${i + 1}` })),
    [form.activity.routes, t],
  );

  // Issuance status for the reward at form index `i` (keyed by its DSL key).
  const issuanceForIndex = (i) => {
    const key = rewardKeys[i];
    return key ? issuances[key] : undefined;
  };

  // --- actions -------------------------------------------------------------
  const persistDraft = async () => {
    const rawInput = isNL ? { prompt } : formToRawInput(form);
    return updateConfig(id, { rawInput, name: isNL ? '' : form.activity.name });
  };

  // Ask a reward's sponsor to confirm issuance. Auto-saves the draft first so
  // the reward + sponsor link are persisted, then triggers the sponsor
  // notification and refreshes the issuance chips.
  const handleRequestIssuance = async (i) => {
    setBusy(true); setError(null); setMsg(null);
    const saved = await persistDraft();
    if (!saved.success) { setBusy(false); setError(saved.error || t('common.saveFailed')); return; }
    const out = await requestRewardIssuance(id, i);
    if (out.success) {
      const fresh = await getConfig(id);
      if (fresh.success && fresh.config) {
        setConfig(fresh.config);
        setForm(rawInputToForm(fresh.config.rawInput));
        setRewardKeys(fresh.config.rewardKeys || []);
        setIssuances(fresh.config.rewardIssuances || {});
      }
      setMsg(out.reused ? t('activities.issueResent') : t('activities.issueSent'));
    } else {
      const map = { NO_SPONSOR: t('activities.issueNoSponsor'), REWARD_NOT_FOUND: t('activities.issueRewardNotFound') };
      setError(map[out.error] || out.error || t('common.saveFailed'));
    }
    setBusy(false);
  };

  // Quietly re-fetch only the issuance status. Does NOT touch the form, so it
  // never clobbers unsaved edits — safe to call from the manual refresh button
  // and the background poll.
  const refreshIssuances = useCallback(async () => {
    setIssRefreshing(true);
    const fresh = await getConfig(id);
    if (fresh.success && fresh.config) {
      setRewardKeys(fresh.config.rewardKeys || []);
      setIssuances(fresh.config.rewardIssuances || {});
    }
    setIssRefreshing(false);
  }, [id]);

  // Auto-update: while any reward is awaiting sponsor confirmation, poll every
  // 8s so the chip flips to Confirmed/Declined once the sponsor acts in the app
  // — no manual reload required. Stops as soon as nothing is pending.
  const hasPendingIssuance = useMemo(
    () => Object.values(issuances).some((v) => v?.status === 'pending'),
    [issuances],
  );
  useEffect(() => {
    if (!hasPendingIssuance) return undefined;
    const timer = setInterval(() => { void refreshIssuances(); }, 8000);
    return () => clearInterval(timer);
  }, [hasPendingIssuance, refreshIssuances]);

  // Autosave the working form/prompt to localStorage on every change (once the
  // page has hydrated). No debounce: writes are tiny and synchronous, which
  // also avoids races with the persist handlers that clear the cache.
  useEffect(() => {
    if (!hydratedRef.current || !cacheKey) return;
    try {
      window.localStorage.setItem(cacheKey, JSON.stringify({ form, prompt, savedAt: Date.now() }));
    } catch { /* quota / private mode — ignore */ }
  }, [form, prompt, cacheKey]);

  const handleSaveDraft = async () => {
    setBusy(true); setError(null); setMsg(null);
    const out = await persistDraft();
    setBusy(false);
    if (out.success) { setMsg(t('activities.draftSaved')); setConfig(out.config); clearDraftCache(); }
    else setError(out.error || t('common.saveFailed'));
  };

  const handleUpdatePublished = async () => {
    setBusy(true);
    setError(null);
    setMsg(null);
    const body = isNL
      ? { sourceType: 'natural_language', prompt }
      : { sourceType: 'template', rawInput: formToRawInput(form), name: form.activity.name };
    const out = await updatePublishedConfig(id, body);
    setBusy(false);
    if (!out.success) {
      setError(
        out.error === 'HAS_ERRORS'
          ? ((out.errors || []).join('；') || t('activities.hasErrors'))
          : (out.error || t('activities.updatePublishedFailed')),
      );
      return;
    }
    clearDraftCache();
    setEditingPublished(false);
    setMsg(t('activities.updatePublishedDone'));
    await load();
  };

  // "创建" (Create): save all form data, compile it into runtime rules, then
  // branch on whether the activity has route data:
  //   • no routes  → publish immediately (goes live)
  //   • has routes → stays a compiled draft; the creator finishes route node
  //     placement in the app (the activity appears in their app draft box),
  //     which then publishes it. See handlePublish for the manual re-publish.
  // Route-based activities can't go live from the web — they need node placement
  // in the app first. Register the linked route-setup draft (so the activity
  // shows up in the creator's app draft box) and pop up a reminder. Shared by
  // both the "创建" button and the preview "确认上架" button so neither can
  // accidentally publish a route activity without its route.
  // AI co-creation: generate (or refine, when `feedback` is provided) a route's
  // playable draft — title, synopsis, and a riddle for each waypoint — and merge
  // the result back into the form aligned to the drawn waypoints.
  const handleAiRouteDraft = async (routeIdx, feedback = '') => {
    const route = (form.activity.routes || [])[routeIdx];
    if (!route) return;
    const waypoints = (route.waypoints || [])
      .filter((w) => Number.isFinite(w.lat) && Number.isFinite(w.lng))
      .map((w) => ({ name: w.name || '', lat: w.lat, lng: w.lng }));
    if (waypoints.length === 0) { setError(t('activities.routeNeedsWaypoints')); return; }
    setRouteAiBusy(routeIdx); setError(null); setMsg(null);
    try {
      const existingNodes = (route.waypoints || []).map((w) => ({
        question: w.riddle?.question || '', answer: w.riddle?.answer || '', hint: w.riddle?.hint || '',
      }));
      const res = await aiRouteDraft(id, {
        waypoints,
        routeName: route.name || '',
        title: route.title || '',
        synopsis: route.synopsis || '',
        city: (form.activity.locations || [])[0]?.name || form.activity.location || '',
        activityName: form.activity.name || '',
        activityDescription: form.activity.description || '',
        language: language === 'en' ? 'en' : 'zh',
        difficulty: 'medium',
        userFeedback: feedback || '',
        existingNodes: feedback ? existingNodes : [],
        theme: route.theme || '',
        routeStyle: route.routeStyle || 'cultural_art',
        inspiration: route.inspiration || '',
        preferredNodeTypes: (route.preferredNodeTypes && route.preferredNodeTypes.length)
          ? route.preferredNodeTypes
          : ROUTE_NODE_TYPE_OPTIONS.map((o) => o.value),
      }).catch((e) => ({ success: false, error: e?.message }));
      if (!res.success) {
        setError(res.error === 'AI_CREATION_PREMIUM_REQUIRED' ? t('activities.aiPremiumOnly') : (res.error || t('activities.aiDraftFailed')));
        return;
      }
      const nodes = Array.isArray(res.nodes) ? res.nodes : [];
      setForm((f) => ({
        ...f,
        activity: {
          ...f.activity,
          routes: (f.activity.routes || []).map((r, i) => {
            if (i !== routeIdx) return r;
            const waypoints2 = (r.waypoints || []).map((w, j) => {
              const n = nodes[j];
              if (!n) return w;
              return {
                ...w,
                name: w.name || n.locationName || '',
                riddle: { question: n.question || '', answer: n.answer || '', hint: n.hint || '', clueType: n.clueType || null },
              };
            });
            return { ...r, title: res.title || r.title || '', synopsis: res.synopsis || r.synopsis || '', waypoints: waypoints2 };
          }),
        },
      }));
      clearDraftCache();
      setRouteAiFeedback((s) => ({ ...s, [routeIdx]: '' }));
      setMsg(t('activities.aiDraftDone'));
    } catch (e) {
      setError(e?.message || t('activities.aiDraftFailed'));
    } finally {
      setRouteAiBusy(null);
    }
  };

  const startRouteSetup = async () => {
    // Finalize every route: fully-virtual routes become live scripts on the web;
    // routes with physical nodes are handed to the app draft box for on-site
    // burial. The activity publishes once all routes are finalized.
    const out = await finalizeRoutes(id, { language: language === 'en' ? 'en' : 'zh' })
      .catch((e) => ({ success: false, error: e?.message }));
    if (out && out.success) {
      clearDraftCache();
      await load();
      if (out.needsApp) {
        const msgText = t('activities.createdNeedRouteSetup');
        setMsg(msgText);
        window.alert(msgText); // reminder can't be missed
      } else {
        setMsg(t('activities.routesPublished'));
      }
      return true;
    }
    const err = out?.error;
    setError(err === 'NO_ROUTES' ? t('activities.publishFailed') : (err || t('activities.publishFailed')));
    return false;
  };

  const handleCreate = async () => {
    setBusy(true); setError(null); setMsg(null);
    const body = isNL
      ? { sourceType: 'natural_language', prompt }
      : { sourceType: 'template', rawInput: formToRawInput(form), name: form.activity.name };
    const compiled = await compileConfig(id, body);
    if (!compiled.success) {
      setBusy(false);
      setError(compiled.error || (compiled.errors ? compiled.errors.join('；') : t('activities.compileFailed')));
      return;
    }
    if ((form.activity.routes || []).length > 0) {
      await startRouteSetup();
      setBusy(false);
      return;
    }
    const pub = await publishConfig(id, compiled.config?.eventId || config?.eventId || undefined);
    setBusy(false);
    if (pub.success) { setMsg(t('activities.published')); clearDraftCache(); await load(); }
    else setError(pub.error === 'HAS_ERRORS' ? t('activities.hasErrors') : pub.error === 'EVENT_LOCATION_REQUIRED' ? t('activities.publishNeedLocation') : pub.error || t('activities.publishFailed'));
  };

  const handlePublish = async () => {
    setBusy(true); setError(null); setMsg(null);
    // Route activities never publish directly — divert to the app route setup.
    if ((form.activity.routes || []).length > 0) {
      await startRouteSetup();
      setBusy(false);
      return;
    }
    const out = await publishConfig(id, config?.eventId || undefined);
    setBusy(false);
    if (out.success) { setMsg(t('activities.published')); clearDraftCache(); await load(); }
    else setError(out.error === 'HAS_ERRORS' ? t('activities.hasErrors') : out.error === 'EVENT_LOCATION_REQUIRED' ? t('activities.publishNeedLocation') : out.error || t('activities.publishFailed'));
  };

  const handleArchive = async () => {
    setBusy(true); setError(null);
    const out = await archiveConfig(id);
    setBusy(false);
    if (out.success) { setMsg(t('activities.archived')); clearDraftCache(); await load(); } else setError(out.error || t('activities.archiveFailed'));
  };

  const handleDelete = async () => {
    if (!window.confirm(t('activities.confirmDelete'))) return;
    setBusy(true);
    const out = await deleteConfig(id);
    setBusy(false);
    if (out.success) { clearDraftCache(); navigate(basePath); }
    else setError(out.error || t('common.deleteFailed'));
  };

  // The reward-economy simulator is a live client-side model (see the JSX in
  // section 03b): the creator enters how many participants will complete each
  // action; the aggregate points minted, reward cost and budget balance update
  // instantly. No server round-trip needed.
  const setSimCount = (idx, val) => setSimCounts((c) => ({ ...c, [idx]: val }));

  const handleOptimize = async (rawMessage) => {
    const message = String(rawMessage ?? optimizeInput).trim();
    setOptimizeBusy(true); setError(null);
    setOptimizeChat((c) => [...c, { role: 'user', text: message || t('activities.optimizeAutoAsk') }]);
    setOptimizeInput('');
    const out = await optimizePointRules(id, {
      context: optimizerContextFromForm(form),
      message,
      language: language === 'en' ? 'en' : 'zh',
    });
    setOptimizeBusy(false);
    if (out.success) {
      setOptimizeChat((c) => [...c, { role: 'ai', text: out.reply || '', rationale: out.rationale || '' }]);
      setOptimizeSuggestion(Array.isArray(out.pointRules) && out.pointRules.length ? out.pointRules : null);
    } else {
      const errText = out.error === 'AI_CREATION_PREMIUM_REQUIRED'
        ? t('activities.aiPremiumOnly')
        : (out.error || t('activities.optimizeFailed'));
      setOptimizeChat((c) => [...c, { role: 'ai', text: `⚠ ${errText}` }]);
    }
  };

  const applyOptimizedRules = () => {
    if (!optimizeSuggestion) return;
    setForm((f) => ({
      ...f,
      pointRules: optimizeSuggestion.map((r) => ({
        action: r.action,
        description: r.description || '',
        points: r.points ?? 0,
        dailyLimit: r.dailyLimit ?? '',
        totalLimit: r.totalLimit ?? '',
        verification: r.verification || 'gps',
      })),
    }));
    setOptimizeSuggestion(null);
    setMsg(t('activities.optimizeApplied'));
  };

  const handleDraw = async () => {
    setBusy(true); setError(null);
    const out = await drawLottery(id);
    setBusy(false);
    if (out.success) { setMsg(t('activities.drawDone', { n: out.totalWinners ?? 0 })); getStats(id).then((s) => s.success && setStats(s.stats)); }
    else setError(out.error || t('activities.drawFailed'));
  };

  if (loading) return <p>{t('common.loading')}</p>;
  if (!config) return <ErrorText>{error || t('activities.notFound')}</ErrorText>;

  const preview = config.preview;
  const pct = (value) => `${Math.round((Number(value) || 0) * 100)}%`;
  const durationText = (seconds) => {
    const total = Math.max(0, Math.round(Number(seconds) || 0));
    if (total < 60) return `${total}s`;
    const hours = Math.floor(total / 3600);
    const minutes = Math.round((total % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  return (
    <>
      <BtnRow style={{ marginTop: 0, marginBottom: 12 }}>
        <Btn type="button" onClick={() => navigate(basePath)}>{t('common.backToList')}</Btn>
        <Badge $status={published ? 'active' : config.status === 'archived' ? 'ended' : 'draft'} style={{ alignSelf: 'center' }}>
          {statusLabel(config.status, language)}
        </Badge>
      </BtnRow>
      <PageTitle>{form.activity.name || config.name || t('activities.editorUnnamed')}</PageTitle>
      <PageSubtitle>
        {isNL ? t('activities.subtitleNL') : t('activities.subtitleTpl')}
      </PageSubtitle>

      {restoredFromCache ? (
        <RestoreBanner>
          <span>{t('activities.draftRestored')}</span>
          <button type="button" onClick={() => { clearDraftCache(); load(); }}>
            {t('activities.draftDiscard')}
          </button>
        </RestoreBanner>
      ) : null}
      {msg ? <p style={{ color: colors.accent, fontWeight: 700 }}>{msg}</p> : null}
      {error ? <ErrorText>{error}</ErrorText> : null}

      {/* Ready-to-publish panel (after compile). The AI-generated rule preview
          was removed; the reward/point simulator now lives in the editor form
          (see section 03b). Compile errors/warnings remain as a publish gate. */}
      {compiled && preview && !editingPublished ? (
        <PreviewPanel>
          <SectionTitle>
            {livePublished ? t('activities.publishedSuccess') : t('activities.readyToPublish')}
          </SectionTitle>
          {Array.isArray(config.compileErrors) && config.compileErrors.length > 0 ? (
            config.compileErrors.map((e, i) => <ErrorText key={i}>⚠ {e}</ErrorText>)
          ) : null}
          {Array.isArray(config.compileWarnings) && config.compileWarnings.map((w, i) => <Warn key={i}>· {w}</Warn>)}

          <BtnRow>
            {!livePublished ? (
              <>
                <Btn $primary type="button" onClick={handlePublish} disabled={busy || (config.compileErrors || []).length > 0}>
                  {(form.activity.routes || []).length > 0 ? t('activities.goRouteSetup') : t('activities.confirmPublish')}
                </Btn>
                <Btn type="button" onClick={() => { setConfig((c) => ({ ...c, status: 'draft' })); window.scrollTo({ top: 300, behavior: 'smooth' }); }}>
                  {t('activities.editRules')}
                </Btn>
              </>
            ) : (
              <>
                <Btn $primary type="button" onClick={() => {
                  setEditingPublished(true);
                  setError(null);
                  setMsg(null);
                  window.scrollTo({ top: 300, behavior: 'smooth' });
                }}>
                  {t('activities.editPublished')}
                </Btn>
                <Btn type="button" onClick={handleArchive} disabled={busy}>{t('activities.unpublish')}</Btn>
              </>
            )}
          </BtnRow>
        </PreviewPanel>
      ) : null}

      {/* Published: stats + lottery */}
      {livePublished && !editingPublished && stats ? (
        <Section>
          <SectionTitle>{t('activities.ops')}</SectionTitle>
          <SectionHint>{t('activities.opsHint')}</SectionHint>
          <StatGrid>
            <StatCard><StatLabel>{t('activities.registrations')}</StatLabel><StatValue>{stats.registrations || 0}</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.participants')}</StatLabel><StatValue>{stats.participants}</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.active7d')}</StatLabel><StatValue>{stats.activeParticipants7d || 0}</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.engagementRate')}</StatLabel><StatValue>{pct(stats.engagementRate)}</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.pointsAwarded')}</StatLabel><StatValue>{stats.totalPointsAwarded}</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.routeCompletions')}</StatLabel><StatValue>{stats.totalRouteCompletions || 0}</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.routeCompletionRate')}</StatLabel><StatValue>{pct(stats.routeCompletionRate)}</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.totalDistanceEstimated')}</StatLabel><StatValue>{Number(stats.estimatedTotalDistanceKm || 0).toFixed(1)} km</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.avgDistanceEstimated')}</StatLabel><StatValue>{Number(stats.estimatedAverageDistanceKm || 0).toFixed(1)} km</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.avgRouteDuration')}</StatLabel><StatValue>{durationText(stats.averageRouteDurationSeconds)}</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.redemptions')}</StatLabel><StatValue>{stats.redemptions || 0}</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.tickets')}</StatLabel><StatValue>{stats.lotteryTickets}</StatValue></StatCard>
            <StatCard><StatLabel>{t('activities.winners')}</StatLabel><StatValue>{stats.lotteryWinners}</StatValue></StatCard>
          </StatGrid>
          <SectionHint style={{ marginTop: 8 }}>{t('activities.distanceMethodHint')}</SectionHint>

          <SectionTitle style={{ marginTop: 18 }}>{t('activities.routeAnalytics')}</SectionTitle>
          <AnalyticsTable>
            <table>
              <thead>
                <tr>
                  <th>{t('activities.analyticsName')}</th>
                  <th>{t('activities.analyticsParticipants')}</th>
                  <th>{t('activities.analyticsCompletions')}</th>
                  <th>{t('activities.analyticsCompletionRate')}</th>
                  <th>{t('activities.analyticsAvgDuration')}</th>
                  <th>{t('activities.analyticsRouteKm')}</th>
                  <th>{t('activities.analyticsTotalKm')}</th>
                </tr>
              </thead>
              <tbody>
                {(stats.routes || []).length ? stats.routes.map((route) => (
                  <tr key={route.routeId || route.scriptId}>
                    <td>{route.name}</td>
                    <td>{route.participants}</td>
                    <td>{route.completions}</td>
                    <td>{pct(route.completionRate)}</td>
                    <td>{durationText(route.averageDurationSeconds)}</td>
                    <td>{Number(route.routeDistanceKm || 0).toFixed(1)}</td>
                    <td>{Number(route.estimatedTotalDistanceKm || 0).toFixed(1)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="7">{t('activities.analyticsNoData')}</td></tr>
                )}
              </tbody>
            </table>
          </AnalyticsTable>

          <SectionTitle style={{ marginTop: 18 }}>{t('activities.sessionAnalytics')}</SectionTitle>
          <AnalyticsTable>
            <table>
              <thead>
                <tr>
                  <th>{t('activities.analyticsName')}</th>
                  <th>{t('activities.analyticsSchedule')}</th>
                  <th>{t('activities.analyticsParticipants')}</th>
                  <th>{t('activities.analyticsCheckins')}</th>
                  <th>{t('activities.analyticsScheduledDuration')}</th>
                  <th>{t('activities.analyticsParticipationTime')}</th>
                  <th>{t('activities.pointsAwarded')}</th>
                </tr>
              </thead>
              <tbody>
                {(stats.sessions || []).length ? stats.sessions.map((session) => (
                  <tr key={session.sessionId}>
                    <td>{session.name}</td>
                    <td>{[session.date, [session.startTime, session.endTime].filter(Boolean).join('–')].filter(Boolean).join(' · ') || '—'}</td>
                    <td>{session.participants}</td>
                    <td>{session.checkins}</td>
                    <td>{session.scheduledDurationMinutes ? `${session.scheduledDurationMinutes} min` : '—'}</td>
                    <td>{session.estimatedTotalParticipationMinutes ? `${session.estimatedTotalParticipationMinutes} min` : '—'}</td>
                    <td>{session.pointsAwarded}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="7">{t('activities.analyticsNoData')}</td></tr>
                )}
              </tbody>
            </table>
          </AnalyticsTable>

          <Row $cols="1fr 1fr" style={{ marginTop: 18, alignItems: 'start' }}>
            <div>
              <SectionTitle>{t('activities.actionAnalytics')}</SectionTitle>
              {(stats.actionBreakdown || []).length ? stats.actionBreakdown.map((action) => (
                <div key={action.action} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, padding: '6px 0', borderBottom: `1px solid ${colors.border}`, fontSize: 12 }}>
                  <span>{tEnum('action', action.action)}</span>
                  <span>{action.participants} {t('activities.people')} · {action.occurrences} {t('activities.times')} · {action.points} pts</span>
                </div>
              )) : <SectionHint>{t('activities.analyticsNoData')}</SectionHint>}
            </div>
            <div>
              <SectionTitle>{t('activities.heatTrend')}</SectionTitle>
              {(stats.dailyParticipants || []).length ? (() => {
                const max = Math.max(1, ...(stats.dailyParticipants || []).map((d) => Number(d.participants) || 0));
                return stats.dailyParticipants.map((day) => (
                  <div key={day.date} style={{ display: 'grid', gridTemplateColumns: '82px 1fr 30px', gap: 8, alignItems: 'center', marginBottom: 6, fontSize: 11 }}>
                    <span>{day.date.slice(5)}</span>
                    <div style={{ height: 8, borderRadius: 999, background: colors.bgAlt, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.max(4, ((Number(day.participants) || 0) / max) * 100)}%`, height: '100%', background: colors.accent }} />
                    </div>
                    <b>{day.participants}</b>
                  </div>
                ));
              })() : <SectionHint>{t('activities.analyticsNoData')}</SectionHint>}
            </div>
          </Row>
          <BtnRow style={{ marginTop: 0 }}>
            <Btn type="button" onClick={handleDraw} disabled={busy}>{t('activities.draw')}</Btn>
          </BtnRow>
        </Section>
      ) : null}

      {/* Editor form — hidden once published */}
      {!published ? (
        isNL ? (
          <Section>
            <SectionTitle>{t('activities.nlHeading')}</SectionTitle>
            <SectionHint>
              {t('activities.nlHint')}
            </SectionHint>
            <Field>
              {t('activities.activityDesc')}
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} style={{ minHeight: 120 }} />
            </Field>
          </Section>
        ) : (
          <>
            {/* 01 Activity Information */}
            <Section>
              <SectionTitle>{t('activities.sec01')}</SectionTitle>
              <Row $cols="repeat(auto-fit, minmax(200px, 1fr))">
                <Field>{t('activities.fName')}<input value={form.activity.name} onChange={(e) => setActivity('name', e.target.value)} /></Field>
                <Field>
                  {t('activities.fType')}
                  <select value={form.activity.activityType} onChange={(e) => setActivity('activityType', e.target.value)}>
                    {!GATHERING_TAG_KEYS.includes(form.activity.activityType) && form.activity.activityType ? (
                      <option value={form.activity.activityType}>{form.activity.activityType}</option>
                    ) : null}
                    {GATHERING_TAG_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{tEnum('gatheringTag', o.value)}</option>
                    ))}
                  </select>
                </Field>
              </Row>
              <div style={{ marginBottom: 14 }}>
                <ActivityMediaField
                  items={form.activity.mediaUrls}
                  disabled={published}
                  onChange={(mediaUrls) => setActivity('mediaUrls', mediaUrls)}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                  {t('activities.geoLabel')}<span style={{ fontWeight: 400, color: colors.inkMuted }}>{t('activities.geoHint')}</span>
                </div>
                <GeoDesigner
                  value={{ locations: form.activity.locations, routes: form.activity.routes }}
                  onChange={(geo) => setForm((f) => ({
                    ...f,
                    activity: { ...f.activity, locations: geo.locations, routes: geo.routes },
                  }))}
                  disabled={published}
                />
              </div>
              <Field>{t('activities.fDesc')}<textarea value={form.activity.description} onChange={(e) => setActivity('description', e.target.value)} /></Field>
              <Row $cols="repeat(auto-fit, minmax(160px, 1fr))">
                <Field>{t('activities.fStart')}<input type="date" value={form.activity.startDate} onChange={(e) => setActivity('startDate', e.target.value)} /></Field>
                <Field>{t('activities.fEnd')}<input type="date" value={form.activity.endDate} onChange={(e) => setActivity('endDate', e.target.value)} /></Field>
                <Field>{t('activities.fRedeemDeadline')}<input type="date" value={form.activity.redemptionDeadline} onChange={(e) => setActivity('redemptionDeadline', e.target.value)} /></Field>
                <Field>{t('activities.fTarget')}<input type="number" value={form.activity.targetParticipants} onChange={(e) => setActivity('targetParticipants', e.target.value)} /></Field>
              </Row>
            </Section>

            {/* 01a Route co-creation workbench (AI console ported from the app) */}
            {(form.activity.routes || []).length > 0 ? (
              <Section>
                <SectionTitle>{t('activities.secRouteWorkbench')}</SectionTitle>
                <SectionHint>{t('activities.routeWorkbenchHint')}</SectionHint>
                {(form.activity.routes || []).map((route, ri) => {
                  const nodes = route.waypoints || [];
                  const physicalCount = nodes.filter((n) => n.isPhysical === true).length;
                  const virtualPlaced = nodes.filter((n) => n.isPhysical !== true && Number.isFinite(n.lat) && (n.riddle?.answer || '').trim()).length;
                  const virtualTotal = nodes.filter((n) => n.isPhysical !== true).length;
                  return (
                    <RepeatItem key={route.id || ri}>
                      <div style={{ fontWeight: 800, marginBottom: 8 }}>
                        {route.name || `${t('activities.geoRoute')} ${ri + 1}`} · {nodes.length} {t('activities.rwNodes')}
                      </div>
                      <Row $cols="1fr 1fr">
                        <Field>{t('activities.rwRouteTitle')}<input value={route.title || ''} onChange={(e) => setRouteField(ri, 'title', e.target.value)} disabled={published} /></Field>
                        <Field>{t('activities.rwRouteSynopsis')}<input value={route.synopsis || ''} onChange={(e) => setRouteField(ri, 'synopsis', e.target.value)} disabled={published} /></Field>
                      </Row>
                      {/* Step-2 creation preferences (ported from the app's「灵感描述」) */}
                      <Field style={{ marginTop: 8 }}>{t('activities.rwTheme')}<input value={route.theme || ''} onChange={(e) => setRouteField(ri, 'theme', e.target.value)} placeholder={t('activities.rwThemePh')} disabled={published} /></Field>
                      <div style={{ fontSize: 12, fontWeight: 700, margin: '8px 0 4px' }}>{t('activities.rwStyle')}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                        {ROUTE_STYLE_OPTIONS.map((opt) => {
                          const sel = (route.routeStyle || 'cultural_art') === opt.key;
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              onClick={() => setRouteField(ri, 'routeStyle', opt.key)}
                              disabled={published}
                              style={{
                                border: `1px solid ${sel ? colors.accent : colors.border}`,
                                background: sel ? colors.accentSoft : colors.bgAlt,
                                color: colors.ink, borderRadius: 999, padding: '5px 10px',
                                fontSize: 12, fontWeight: sel ? 800 : 500, cursor: published ? 'default' : 'pointer',
                              }}
                            >
                              {opt.emoji} {language === 'en' ? opt.en : opt.zh}
                            </button>
                          );
                        })}
                      </div>
                      {(route.routeStyle || 'cultural_art') === 'limited_time' ? (
                        <Field style={{ marginTop: 4 }}>
                          {t('activities.rwDeadline')}
                          <input
                            type="datetime-local"
                            value={route.limitedTimeDeadline || ''}
                            onChange={(e) => setRouteField(ri, 'limitedTimeDeadline', e.target.value)}
                            disabled={published}
                          />
                          <span style={{ fontSize: 11, color: colors.inkMuted, marginTop: 2 }}>{t('activities.rwDeadlineHint')}</span>
                        </Field>
                      ) : null}
                      <div style={{ fontSize: 12, fontWeight: 700, margin: '8px 0 4px' }}>{t('activities.rwNodeTypes')}</div>
                      <div style={{ fontSize: 11, color: colors.inkMuted, marginBottom: 4 }}>{t('activities.rwNodeTypesHint')}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                        {ROUTE_NODE_TYPE_OPTIONS.map((opt) => {
                          const cur = (route.preferredNodeTypes && route.preferredNodeTypes.length) ? route.preferredNodeTypes : DEFAULT_NODE_TYPES;
                          const sel = cur.includes(opt.value);
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => toggleRouteNodeType(ri, opt.value)}
                              disabled={published}
                              style={{
                                border: `1px solid ${sel ? colors.accent : colors.border}`,
                                background: sel ? colors.accentSoft : colors.bgAlt,
                                color: colors.ink, borderRadius: 999, padding: '5px 10px',
                                fontSize: 12, fontWeight: sel ? 800 : 500, cursor: published ? 'default' : 'pointer',
                              }}
                            >
                              {opt.emoji} {language === 'en' ? opt.en : opt.zh}
                            </button>
                          );
                        })}
                      </div>
                      <Field>{t('activities.rwInspiration')}<textarea rows={2} value={route.inspiration || ''} onChange={(e) => setRouteField(ri, 'inspiration', e.target.value)} placeholder={t('activities.rwInspirationPh')} disabled={published} /></Field>
                      <BtnRow style={{ margin: '8px 4px' }}>
                        <Btn type="button" onClick={() => handleAiRouteDraft(ri)} disabled={published || routeAiBusy !== null}>
                          {routeAiBusy === ri ? t('activities.rwAiGenerating') : t('activities.rwAiGenerate')}
                        </Btn>
                        <span style={{ fontSize: 11, color: colors.inkMuted, alignSelf: 'center' }}>{t('activities.rwManualNote')}</span>
                      </BtnRow>
                      {nodes.map((n, ni) => (
                        <div key={ni} style={{ border: `1px solid ${colors.border}`, borderRadius: 10, padding: 10, marginBottom: 8, background: colors.bgAlt }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8 }}>
                            <div style={{ fontWeight: 700, fontSize: 13 }}>
                              {ni + 1}. {n.name || `${t('activities.rwNode')} ${ni + 1}`}
                              {Number.isFinite(n.lat) ? '' : <span style={{ color: '#b45309', fontWeight: 400 }}> · {t('activities.rwNodeNoGps')}</span>}
                            </div>
                            <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', whiteSpace: 'nowrap' }}>
                              <input type="checkbox" checked={n.isPhysical === true} onChange={(e) => setRouteNode(ri, ni, { isPhysical: e.target.checked })} disabled={published} />
                              {t('activities.rwNodePhysical')}
                            </label>
                          </div>
                          <Field>{t('activities.rwNodeQuestion')}<textarea rows={2} value={n.riddle?.question || ''} onChange={(e) => setRouteNodeRiddle(ri, ni, 'question', e.target.value)} disabled={published} /></Field>
                          <Row $cols="1fr 1fr">
                            <Field>{t('activities.rwNodeAnswer')}<input value={n.riddle?.answer || ''} onChange={(e) => setRouteNodeRiddle(ri, ni, 'answer', e.target.value)} disabled={published} /></Field>
                            <Field>{t('activities.rwNodeHint')}<input value={n.riddle?.hint || ''} onChange={(e) => setRouteNodeRiddle(ri, ni, 'hint', e.target.value)} disabled={published} /></Field>
                          </Row>
                          <div style={{ fontSize: 11, color: n.isPhysical ? '#b45309' : colors.inkMuted }}>
                            {n.isPhysical ? t('activities.rwNodePhysicalHint') : t('activities.rwNodeVirtualHint')}
                          </div>
                          <NodeMediaField
                            value={n.media || null}
                            disabled={published}
                            onChange={(media) => setRouteNode(ri, ni, { media })}
                          />
                        </div>
                      ))}
                      <Row $cols="1fr auto">
                        <Field>{t('activities.rwAiRefine')}<input value={routeAiFeedback[ri] || ''} onChange={(e) => setRouteAiFeedback((s) => ({ ...s, [ri]: e.target.value }))} placeholder={t('activities.rwAiRefinePh')} disabled={published} /></Field>
                        <Btn type="button" onClick={() => { const fb = (routeAiFeedback[ri] || '').trim(); if (fb) handleAiRouteDraft(ri, fb); }} disabled={published || routeAiBusy !== null}>{t('activities.rwAiRefineSend')}</Btn>
                      </Row>
                      <div style={{ fontSize: 12, marginTop: 8, fontWeight: 700, color: physicalCount > 0 ? '#b45309' : '#166534' }}>
                        {physicalCount > 0 ? t('activities.rwRouteNeedsApp') : t('activities.rwRouteWebComplete')}
                        {' · '}{t('activities.rwVirtualPlaced')}: {virtualPlaced}/{virtualTotal}
                        {physicalCount > 0 ? ` · ${t('activities.rwPhysicalCount')}: ${physicalCount}` : ''}
                      </div>
                    </RepeatItem>
                  );
                })}
              </Section>
            ) : null}

            {/* 01b Timed sub-activities (schedule) */}
            <Section>
              <SectionTitle>{t('activities.secSessions')}</SectionTitle>
              <SectionHint>{t('activities.secSessionsHint')}</SectionHint>
              {(form.activity.sessions || []).map((s, i) => (
                <RepeatItem key={s.id || i}>
                  <Row $cols="repeat(auto-fit, minmax(160px, 1fr))">
                    <Field>{t('activities.fSessionName')}<input value={s.name} onChange={(e) => setSession(i, 'name', e.target.value)} disabled={published} /></Field>
                    <Field>{t('activities.fSessionDate')}<input type="date" value={s.date} onChange={(e) => setSession(i, 'date', e.target.value)} disabled={published} /></Field>
                    <Field>{t('activities.fSessionStart')}<input type="time" value={s.startTime} onChange={(e) => setSession(i, 'startTime', e.target.value)} disabled={published} /></Field>
                    <Field>{t('activities.fSessionEnd')}<input type="time" value={s.endTime} onChange={(e) => setSession(i, 'endTime', e.target.value)} disabled={published} /></Field>
                  </Row>
                  <Row $cols="repeat(auto-fit, minmax(180px, 1fr))">
                    <Field>
                      {t('activities.fSessionLocation')}
                      <select value={s.locationRef} onChange={(e) => setSession(i, 'locationRef', e.target.value)} disabled={published}>
                        <option value="">{t('activities.sessionNoPlace')}</option>
                        {locationOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </Field>
                    <Field>
                      {t('activities.fSessionRoute')}
                      <select value={s.routeRef} onChange={(e) => setSession(i, 'routeRef', e.target.value)} disabled={published}>
                        <option value="">{t('activities.sessionNoRoute')}</option>
                        {routeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </Field>
                    <Field>{t('activities.fSessionDesc')}<input value={s.description} onChange={(e) => setSession(i, 'description', e.target.value)} disabled={published} /></Field>
                  </Row>
                  {!published ? <RemoveLink onClick={() => removeSession(i)}>{t('activities.removeSession')}</RemoveLink> : null}
                </RepeatItem>
              ))}
              {!published ? <Btn type="button" onClick={addSession}>{t('activities.addSession')}</Btn> : null}
            </Section>

            {/* 02 Sponsors */}
            <Section>
              <SectionTitle>{t('activities.sec02')}</SectionTitle>
              <SectionHint>{t('activities.sec02Hint')}</SectionHint>
              {form.sponsors.map((s, i) => {
                const linkedRewards = s.merchantUserId
                  ? form.rewards
                      .map((rw, idx) => ({ rw, idx }))
                      .filter(({ rw }) => rw.sponsorRef && rw.sponsorRef === s.merchantUserId)
                  : [];
                return (
                  <RepeatItem key={i}>
                    <Row $cols="repeat(auto-fit, minmax(180px, 1fr))">
                      <MerchantNameField
                        name={s.merchantName}
                        userId={s.merchantUserId}
                        disabled={published}
                        onNameChange={(v) => setRow('sponsors', i, 'merchantName', v)}
                        onPick={({ merchantName, merchantUserId }) => setForm((f) => ({
                          ...f,
                          sponsors: f.sponsors.map((r, idx) => (idx === i ? { ...r, merchantName, merchantUserId } : r)),
                        }))}
                      />
                      <Field>{t('activities.fSponsorType')}<select value={s.sponsorType} onChange={(e) => setRow('sponsors', i, 'sponsorType', e.target.value)}>{SPONSOR_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{tEnum('sponsorType', o.value)}</option>)}</select></Field>
                      <Field>{t('activities.fBudget')}<input type="number" value={s.rewardBudgetDollars} onChange={(e) => setRow('sponsors', i, 'rewardBudgetDollars', e.target.value)} /></Field>
                    </Row>
                    <SponsorRewards>
                      <div className="hd">{t('activities.sponsorRewardsTitle')}</div>
                      {!s.merchantUserId ? (
                        <div className="empty">{t('activities.sponsorRewardsNeedLink')}</div>
                      ) : linkedRewards.length === 0 ? (
                        <div className="empty">{t('activities.sponsorRewardsEmpty')}</div>
                      ) : (
                        <ul>
                          {linkedRewards.map(({ rw, idx }) => {
                            const st = issuanceForIndex(idx)?.status;
                            return (
                              <li key={idx}>
                                <span className="name">{rw.title || t('activities.untitledReward')}</span>
                                <span className="meta">
                                  {tEnum('rewardKind', rw.kind)}{rw.valueDollars ? ` · $${rw.valueDollars}` : ''} · {tEnum('redemptionMode', rw.mode)}
                                </span>
                                <IssueBadge $issued={st === 'authenticated'} $rejected={st === 'rejected'} $muted={!st}>
                                  {st === 'authenticated'
                                    ? t('activities.issueConfirmed')
                                    : st === 'rejected'
                                      ? t('activities.issueRejected')
                                      : st === 'pending'
                                        ? t('activities.issuePending')
                                        : t('activities.issueNotSent')}
                                </IssueBadge>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </SponsorRewards>
                    {!published ? <RemoveLink onClick={() => removeRow('sponsors', i)}>{t('activities.removeSponsor')}</RemoveLink> : null}
                  </RepeatItem>
                );
              })}
              <Btn type="button" onClick={() => addRow('sponsors', BLANK_SPONSOR)}>{t('activities.addSponsor')}</Btn>
            </Section>

            {/* 03 Point Rules */}
            <Section>
              <SectionTitle>{t('activities.sec03')}</SectionTitle>
              <SectionHint>{t('activities.sec03Hint')}</SectionHint>
              {form.pointRules.map((r, i) => (
                <RepeatItem key={i}>
                  <Row $cols="repeat(auto-fit, minmax(140px, 1fr))">
                    <Field>{t('activities.fAction')}<select value={r.action} onChange={(e) => setPointRuleAction(i, e.target.value)}>
                      {r.action && !ACTION_OPTIONS.some((o) => o.value === r.action) ? (
                        <option value={r.action}>{tEnum('action', r.action)}</option>
                      ) : null}
                      {ACTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{tEnum('action', o.value)}</option>)}
                    </select></Field>
                    <Field>{t('activities.fVerification')}
                      <input
                        value={tEnum('verification', r.verification || ACTION_VERIFICATION[r.action] || 'gps')}
                        readOnly
                        disabled
                        title={t('activities.verificationFixedHint')}
                        style={{ background: colors.bgAlt, color: colors.inkMuted }}
                      />
                    </Field>
                    {r.action === 'session_checkin' ? (
                      <Field>{t('activities.fSessionBind')}
                        <select value={r.sessionRef || ''} onChange={(e) => setRow('pointRules', i, 'sessionRef', e.target.value)}>
                          <option value="">{t('activities.sessionBindAny')}</option>
                          {(form.activity.sessions || []).map((s) => (
                            <option key={s.id} value={s.id}>{s.name || s.id}</option>
                          ))}
                        </select>
                      </Field>
                    ) : null}
                    <Field>{t('activities.fPoints')}<input type="number" value={r.points} onChange={(e) => setRow('pointRules', i, 'points', e.target.value)} /></Field>
                    <Field>{t('activities.fDailyLimit')}<input type="number" value={r.dailyLimit} onChange={(e) => setRow('pointRules', i, 'dailyLimit', e.target.value)} placeholder={t('activities.unlimitedPh')} /></Field>
                    <Field>{t('activities.fTotalLimit')}<input type="number" value={r.totalLimit} onChange={(e) => setRow('pointRules', i, 'totalLimit', e.target.value)} placeholder={t('activities.unlimitedPh')} /></Field>
                    <Field>{t('activities.fRuleDesc')}<input value={r.description} onChange={(e) => setRow('pointRules', i, 'description', e.target.value)} /></Field>
                  </Row>
                  <RemoveLink onClick={() => removeRow('pointRules', i)}>{t('common.remove')}</RemoveLink>
                </RepeatItem>
              ))}
              <Btn type="button" onClick={() => addRow('pointRules', BLANK_POINT_RULE)}>{t('activities.addPointRule')}</Btn>
            </Section>

            {/* 03b Reward / point-rule simulator + AI co-pilot (moved here from the
                post-compile ops panel). Helps the creator balance point rules,
                reward costs and sponsor budget before publishing. */}
            <Section>
              <SectionTitle>{t('activities.simSec')}</SectionTitle>
              <SectionHint>{t('activities.simSecHint')}</SectionHint>

              {/* Aggregate reward-economy simulator: for the expected participant
                  count, the creator enters how many people will complete each
                  action; totals update live to balance budget vs. experience. */}
              {(() => {
                const N = Number(form.activity.targetParticipants) || 0;
                const rules = form.pointRules || [];
                const countFor = (i) => {
                  const raw = simCounts[i];
                  return raw === '' || raw == null ? N : Math.max(0, Number(raw) || 0);
                };
                let totalMinted = 0;
                const rows = rules.map((r, i) => {
                  const pts = Number(r.points) || 0;
                  const cnt = countFor(i);
                  const subtotal = pts * cnt;
                  totalMinted += subtotal;
                  return { i, r, pts, cnt, subtotal };
                });
                const avgPerParticipant = N > 0 ? Math.round(totalMinted / N) : null;
                const rewardCostSum = (form.rewards || []).reduce((s, r) => s + (Number(r.costPoints) || 0), 0);
                const budgetSum = (form.sponsors || []).reduce((s, r) => s + (Number(r.rewardBudgetDollars) || 0), 0);
                const affordable = (form.rewards || []).filter((r) => Number(r.costPoints) > 0 && avgPerParticipant != null && avgPerParticipant >= Number(r.costPoints));
                const HeadCell = ({ children, right }) => (
                  <div style={{ fontSize: 11, fontWeight: 700, color: colors.inkMuted, textAlign: right ? 'right' : 'left' }}>{children}</div>
                );
                return (
                  <>
                    <div style={{ fontSize: 12, color: colors.inkMuted, marginBottom: 8 }}>
                      {t('activities.simTargetLabel')}: <b>{N || '—'}</b>
                    </div>
                    {rules.length === 0 ? (
                      <SectionHint>{t('activities.simNoRules')}</SectionHint>
                    ) : (
                      <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 90px 130px 110px', gap: 8, padding: '8px 10px', background: colors.bgAlt }}>
                          <HeadCell>{t('activities.simActionCol')}</HeadCell>
                          <HeadCell right>{t('activities.simPointsEachCol')}</HeadCell>
                          <HeadCell>{t('activities.simExpectedCol')}</HeadCell>
                          <HeadCell right>{t('activities.simSubtotalCol')}</HeadCell>
                        </div>
                        {rows.map(({ i, r, pts, subtotal }) => (
                          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 90px 130px 110px', gap: 8, padding: '8px 10px', alignItems: 'center', borderTop: `1px solid ${colors.border}` }}>
                            <div style={{ fontSize: 13 }}>
                              {tEnum('action', r.action)}
                              {r.description ? <span style={{ color: colors.inkMuted }}>（{r.description}）</span> : null}
                            </div>
                            <div style={{ textAlign: 'right', fontSize: 13 }}>+{pts}</div>
                            <input
                              type="number"
                              min="0"
                              value={simCounts[i] ?? ''}
                              placeholder={String(N || 0)}
                              onChange={(e) => setSimCount(i, e.target.value)}
                              style={{ width: '100%', padding: '4px 6px', border: `1px solid ${colors.border}`, borderRadius: 6 }}
                            />
                            <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 700 }}>{subtotal.toLocaleString()}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <SimResultBox>
                      <div>
                        {t('activities.simTotalMinted')}：<b>{totalMinted.toLocaleString()}</b>
                        {avgPerParticipant != null ? ` · ${t('activities.simAvgPerParticipant')} ≈ ${avgPerParticipant.toLocaleString()}` : ` · ${t('activities.simSetTarget')}`}
                      </div>
                      <div style={{ color: colors.inkMuted, marginTop: 2 }}>
                        {rewardCostSum > 0 ? `${t('activities.simRewardCost')} ${rewardCostSum.toLocaleString()}` : ''}
                        {budgetSum > 0 ? `${rewardCostSum > 0 ? ' · ' : ''}${t('activities.simBudget')} $${budgetSum.toLocaleString()}` : ''}
                      </div>
                      <div style={{ color: affordable.length ? colors.accent : '#b45309', marginTop: 2 }}>
                        {affordable.length
                          ? t('activities.simAffordable', { list: affordable.map((r) => r.title).join('、') })
                          : t('activities.simNoneAffordable')}
                      </div>
                    </SimResultBox>
                  </>
                );
              })()}

              {/* AI co-pilot chat */}
              <div style={{ marginTop: 14, borderTop: `1px dashed ${colors.border}`, paddingTop: 12 }}>
                <SectionTitle style={{ fontSize: 14 }}>{t('activities.optimizeTitle')}</SectionTitle>
                <SectionHint>{t('activities.optimizeHint')}</SectionHint>
                {optimizeChat.length ? (
                  <ChatBox>
                    {optimizeChat.map((m, i) => (
                      <ChatBubble key={i} $role={m.role}>
                        <div>{m.text}</div>
                        {m.rationale ? <div style={{ opacity: 0.75, marginTop: 4, fontSize: 12 }}>{m.rationale}</div> : null}
                      </ChatBubble>
                    ))}
                    {optimizeBusy ? <ChatBubble $role="ai">{t('activities.optimizeThinking')}</ChatBubble> : null}
                  </ChatBox>
                ) : null}
                {optimizeSuggestion ? (
                  <SuggestionBox>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>{t('activities.optimizeSuggested')}</div>
                    {optimizeSuggestion.map((r, i) => (
                      <div key={i} style={{ fontSize: 13 }}>
                        · {tEnum('action', r.action)} — <b>+{r.points}</b>
                        {r.dailyLimit != null ? ` · ${t('activities.fDailyLimit')} ${r.dailyLimit}` : ''}
                        {r.description ? `（${r.description}）` : ''}
                      </div>
                    ))}
                    <Btn $primary type="button" style={{ marginTop: 8 }} onClick={applyOptimizedRules}>{t('activities.optimizeApply')}</Btn>
                  </SuggestionBox>
                ) : null}
                <Row $cols="1fr 120px" style={{ marginTop: 8, alignItems: 'end' }}>
                  <Field style={{ margin: 0 }}>
                    <textarea
                      rows={2}
                      value={optimizeInput}
                      placeholder={t('activities.optimizePlaceholder')}
                      onChange={(e) => setOptimizeInput(e.target.value)}
                      style={{ resize: 'vertical', width: '100%', fontFamily: 'inherit' }}
                    />
                  </Field>
                  <Btn type="button" onClick={() => handleOptimize()} disabled={optimizeBusy}>{t('activities.optimizeSend')}</Btn>
                </Row>
              </div>
            </Section>

            {/* 03c Participation QR posters: printable codes participants scan at
                the venue to join the activity or check in to a timed mini-activity
                (the QR verification for join_activity / session_checkin rules). */}
            <Section>
              <SectionTitle>{t('activities.qrSec')}</SectionTitle>
              <SectionHint>{t('activities.qrSecHint')}</SectionHint>
              {!published ? <SectionHint style={{ color: '#b45309' }}>{t('activities.qrPublishFirst')}</SectionHint> : null}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 12, marginTop: 8 }}>
                <QrPoster
                  title={t('activities.qrJoinTitle')}
                  subtitle={form.activity.name || ''}
                  payload={buildActivityQrPayload(id)}
                  downloadLabel={t('activities.qrDownload')}
                />
                {(form.activity.sessions || []).filter((s) => s.id).map((s, i) => (
                  <QrPoster
                    key={s.id}
                    title={s.name || `${t('activities.fSessionName')} ${i + 1}`}
                    subtitle={[s.date, [s.startTime, s.endTime].filter(Boolean).join('–')].filter(Boolean).join(' · ')}
                    payload={buildActivityQrPayload(id, s.id)}
                    downloadLabel={t('activities.qrDownload')}
                  />
                ))}
              </div>
            </Section>

            {/* 04 Badges — hidden for now (SHOW_BADGES=false) */}
            {SHOW_BADGES ? (
            <Section>
              <SectionTitle>{t('activities.sec04')}</SectionTitle>
              {form.badges.map((b, i) => (
                <RepeatItem key={i}>
                  <Row $cols="repeat(auto-fit, minmax(140px, 1fr))">
                    <Field>{t('activities.fBadgeName')}<input value={b.name} onChange={(e) => setRow('badges', i, 'name', e.target.value)} /></Field>
                    <Field>{t('activities.fBadgeEmoji')}<input value={b.icon} onChange={(e) => setRow('badges', i, 'icon', e.target.value)} placeholder="🏔️" /></Field>
                    <Field>{t('activities.fBadgeType')}<select value={b.type} onChange={(e) => setRow('badges', i, 'type', e.target.value)}>{BADGE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{tEnum('badgeType', o.value)}</option>)}</select></Field>
                    <Field>{t('activities.fLevel')}<input type="number" value={b.level} onChange={(e) => setRow('badges', i, 'level', e.target.value)} /></Field>
                    <Field>{t('activities.fRequirement')}<select value={b.requirementType} onChange={(e) => setRow('badges', i, 'requirementType', e.target.value)}>{BADGE_REQ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{tEnum('badgeReq', o.value)}</option>)}</select></Field>
                    {(b.requirementType === 'routes_completed' || b.requirementType === 'points_threshold') ? (
                      <Field>{t('activities.fCountThreshold')}<input type="number" value={b.requirementCount} onChange={(e) => setRow('badges', i, 'requirementCount', e.target.value)} /></Field>
                    ) : b.requirementType === 'route_specific' ? (
                      <Field>
                        {t('activities.fRouteId')}
                        <select value={b.routeId} onChange={(e) => setRow('badges', i, 'routeId', e.target.value)} disabled={published}>
                          <option value="">{t('activities.selectRoute')}</option>
                          {b.routeId && !routeOptions.some((o) => o.value === b.routeId) ? (
                            <option value={b.routeId}>{b.routeId}</option>
                          ) : null}
                          {routeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </Field>
                    ) : (
                      <Field>
                        {t('activities.fLocationId')}
                        <select value={b.locationId} onChange={(e) => setRow('badges', i, 'locationId', e.target.value)} disabled={published}>
                          <option value="">{t('activities.selectLocation')}</option>
                          {b.locationId && !locationOptions.some((o) => o.value === b.locationId) ? (
                            <option value={b.locationId}>{b.locationId}</option>
                          ) : null}
                          {locationOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </Field>
                    )}
                  </Row>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{t('activities.fBadgeImage')}</div>
                    <BadgeImageField
                      value={b.iconUrl}
                      disabled={published}
                      onChange={(url) => setRow('badges', i, 'iconUrl', url)}
                    />
                  </div>
                  <RemoveLink style={{ marginTop: 8 }} onClick={() => removeRow('badges', i)}>{t('activities.removeBadge')}</RemoveLink>
                </RepeatItem>
              ))}
              <Btn type="button" onClick={() => addRow('badges', BLANK_BADGE)}>{t('activities.addBadge')}</Btn>
            </Section>
            ) : null}

            {/* 05 Lottery */}
            <Section>
              <SectionTitle>{t('activities.sec05')}</SectionTitle>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, fontSize: 14, fontWeight: 700 }}>
                <input type="checkbox" checked={form.lottery.enabled} onChange={(e) => setLottery('enabled', e.target.checked)} />
                {t('activities.enableLottery')}
              </label>
              {form.lottery.enabled ? (
                <>
                  <SectionHint>{t('activities.lotteryEntryHint')}</SectionHint>
                  <Row $cols="repeat(auto-fit, minmax(150px, 1fr))">
                    <Field>{t('activities.fPointsPerTicket')}<input type="number" value={form.lottery.pointsPerTicket} onChange={(e) => setLottery('pointsPerTicket', e.target.value)} /></Field>
                    <Field>{t('activities.fEntryThreshold')}<input type="number" value={form.lottery.entryRequirementPoints} onChange={(e) => setLottery('entryRequirementPoints', e.target.value)} /></Field>
                    <Field>{t('activities.fMaxEntries')}<input type="number" value={form.lottery.maxEntriesPerUser} onChange={(e) => setLottery('maxEntriesPerUser', e.target.value)} /></Field>
                    <Field>{t('activities.fLotteryDate')}<input type="date" value={form.lottery.lotteryDate} onChange={(e) => setLottery('lotteryDate', e.target.value)} /></Field>
                  </Row>

                  {/* Customizable N points → M tickets exchange tiers */}
                  <div style={{ fontSize: 13, fontWeight: 700, margin: '10px 0 6px' }}>{t('activities.ticketTiersTitle')}</div>
                  <SectionHint>{t('activities.ticketTiersHint')}</SectionHint>
                  {(form.lottery.ticketTiers || []).map((tier, i) => (
                    <RepeatItem key={i}>
                      <Row $cols="repeat(auto-fit, minmax(140px, 1fr))">
                        <Field>{t('activities.fTierPoints')}<input type="number" min="1" value={tier.points} onChange={(e) => setTicketTier(i, 'points', e.target.value)} /></Field>
                        <Field>{t('activities.fTierTickets')}<input type="number" min="1" value={tier.tickets} onChange={(e) => setTicketTier(i, 'tickets', e.target.value)} /></Field>
                      </Row>
                      <RemoveLink onClick={() => removeTicketTier(i)}>{t('activities.removeTier')}</RemoveLink>
                    </RepeatItem>
                  ))}
                  <Btn type="button" onClick={addTicketTier}>{t('activities.addTier')}</Btn>

                  {/* Random ticket drops seeded into route explorations */}
                  <div style={{ fontSize: 13, fontWeight: 700, margin: '16px 0 6px' }}>{t('activities.ticketDropsTitle')}</div>
                  <SectionHint>{t('activities.ticketDropsHint')}</SectionHint>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 10px', fontSize: 13, fontWeight: 700 }}>
                    <input type="checkbox" checked={!!form.lottery.ticketDrops?.enabled} onChange={(e) => setTicketDrop('enabled', e.target.checked)} />
                    {t('activities.enableTicketDrops')}
                  </label>
                  {form.lottery.ticketDrops?.enabled ? (
                    <Row $cols="repeat(auto-fit, minmax(150px, 1fr))">
                      <Field>{t('activities.fDropTotal')}<input type="number" min="1" value={form.lottery.ticketDrops.totalTickets} onChange={(e) => setTicketDrop('totalTickets', e.target.value)} /></Field>
                      <Field>{t('activities.fDropMaxPerUser')}<input type="number" min="1" value={form.lottery.ticketDrops.maxPerUser} onChange={(e) => setTicketDrop('maxPerUser', e.target.value)} /></Field>
                      <Field>{t('activities.fDropRate')}<input type="number" min="0" max="1" step="0.05" value={form.lottery.ticketDrops.dropRate} onChange={(e) => setTicketDrop('dropRate', e.target.value)} placeholder="0.35" /></Field>
                      <Field>{t('activities.fDropStart')}<input type="datetime-local" value={form.lottery.ticketDrops.startAt} onChange={(e) => setTicketDrop('startAt', e.target.value)} /></Field>
                      <Field>{t('activities.fDropEnd')}<input type="datetime-local" value={form.lottery.ticketDrops.endAt} onChange={(e) => setTicketDrop('endAt', e.target.value)} /></Field>
                    </Row>
                  ) : null}

                  <div style={{ fontSize: 13, fontWeight: 700, margin: '16px 0 6px' }}>{t('activities.lotteryPoolsTitle')}</div>
                  <SectionHint>{t('activities.lotteryPoolsHint')}</SectionHint>
                  {(form.lottery.pools || []).map((p, i) => (
                    <RepeatItem key={i}>
                      <Row $cols="repeat(auto-fit, minmax(160px, 1fr))">
                        <Field>{t('activities.fPoolName')}<input value={p.name} onChange={(e) => setLotteryPool(i, 'name', e.target.value)} placeholder={t('activities.poolNamePh')} /></Field>
                        <Field>{t('activities.fPrize')}<input value={p.prize} onChange={(e) => setLotteryPool(i, 'prize', e.target.value)} placeholder="$500 Gear Voucher" /></Field>
                        <Field>{t('activities.fWinnerCount')}<input type="number" value={p.winnerCount} onChange={(e) => setLotteryPool(i, 'winnerCount', e.target.value)} /></Field>
                        <Field>
                          {t('activities.fRewardSponsor')}
                          <select value={p.sponsorRef} onChange={(e) => setLotteryPool(i, 'sponsorRef', e.target.value)}>
                            <option value="">{t('activities.rewardSponsorNone')}</option>
                            {p.sponsorRef && !sponsorOptions.some((o) => o.value === p.sponsorRef) ? (
                              <option value={p.sponsorRef}>{p.sponsorRef}</option>
                            ) : null}
                            {sponsorOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </Field>
                      </Row>
                      <RemoveLink onClick={() => removeLotteryPool(i)}>{t('activities.removePool')}</RemoveLink>
                    </RepeatItem>
                  ))}
                  <Btn type="button" onClick={addLotteryPool}>{t('activities.addPool')}</Btn>
                </>
              ) : null}
            </Section>

            {/* 06 + 07 Rewards */}
            <Section>
              <SectionTitle>{t('activities.sec0607')}</SectionTitle>
              <SectionHint>{t('activities.sec0607Hint')}</SectionHint>
              {form.rewards.map((rw, i) => (
                <RepeatItem key={i}>
                  <Row $cols="repeat(auto-fit, minmax(150px, 1fr))">
                    <Field>{t('activities.fRewardName')}<input value={rw.title} onChange={(e) => setRow('rewards', i, 'title', e.target.value)} /></Field>
                    <Field>
                      {t('activities.fRewardSponsor')}
                      <select value={rw.sponsorRef} onChange={(e) => setRow('rewards', i, 'sponsorRef', e.target.value)}>
                        <option value="">{t('activities.rewardSponsorNone')}</option>
                        {rw.sponsorRef && !sponsorOptions.some((o) => o.value === rw.sponsorRef) ? (
                          <option value={rw.sponsorRef}>{rw.sponsorRef}</option>
                        ) : null}
                        {sponsorOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </Field>
                    {/* Badge-based redemption is hidden for now; rewards are points-only. */}
                    <Field>{t('activities.fCostPoints')}<input type="number" value={rw.costPoints} onChange={(e) => setRow('rewards', i, 'costPoints', e.target.value)} /></Field>
                    <Field>{t('activities.fRewardKind')}<select value={rw.kind} onChange={(e) => setRow('rewards', i, 'kind', e.target.value)}>{REWARD_KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{tEnum('rewardKind', o.value)}</option>)}</select></Field>
                    <Field>{t('activities.fValue')}<input type="number" value={rw.valueDollars} onChange={(e) => setRow('rewards', i, 'valueDollars', e.target.value)} /></Field>
                    <Field>{t('activities.fInventoryPolicy')}<select value={rw.inventoryPolicy} onChange={(e) => setRow('rewards', i, 'inventoryPolicy', e.target.value)}>{INVENTORY_POLICY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{tEnum('inventoryPolicy', o.value)}</option>)}</select></Field>
                    {rw.inventoryPolicy !== 'unlimited' ? (
                      <Field>{t('activities.fInventory')}<input type="number" value={rw.inventory} onChange={(e) => setRow('rewards', i, 'inventory', e.target.value)} /></Field>
                    ) : null}
                    <Field>{t('activities.fPerUserLimit')}<input type="number" value={rw.perUserLimit} onChange={(e) => setRow('rewards', i, 'perUserLimit', e.target.value)} /></Field>
                    <Field>{t('activities.fTotalBudget')}<input type="number" value={rw.totalBudgetDollars} onChange={(e) => setRow('rewards', i, 'totalBudgetDollars', e.target.value)} /></Field>
                    <Field>{t('activities.fExpiryType')}<select value={rw.expiryType} onChange={(e) => setRow('rewards', i, 'expiryType', e.target.value)}>{EXPIRY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{tEnum('expiryType', o.value)}</option>)}</select></Field>
                    {rw.expiryType === 'days' ? (
                      <Field>{t('activities.fDays')}<input type="number" value={rw.expiryValue} onChange={(e) => setRow('rewards', i, 'expiryValue', e.target.value)} /></Field>
                    ) : rw.expiryType === 'fixed_date' ? (
                      <Field>{t('activities.fDate')}<input type="date" value={rw.expiryValue} onChange={(e) => setRow('rewards', i, 'expiryValue', e.target.value)} /></Field>
                    ) : null}
                  </Row>
                  {rw.sponsorRef ? (() => {
                    const iss = issuanceForIndex(i);
                    const st = iss?.status;
                    return (
                      <IssueRow>
                        {st ? (
                          <IssueBadge $issued={st === 'authenticated'} $rejected={st === 'rejected'}>
                            {st === 'authenticated'
                              ? t('activities.issueConfirmed')
                              : st === 'rejected'
                                ? t('activities.issueRejected')
                                : t('activities.issuePending')}
                          </IssueBadge>
                        ) : null}
                        {st ? (
                          <RefreshBtn
                            type="button"
                            onClick={refreshIssuances}
                            disabled={issRefreshing}
                            title={t('activities.issueRefresh')}
                          >
                            <span className={issRefreshing ? 'spin' : undefined} aria-hidden>↻</span>
                            {t('activities.issueRefresh')}
                          </RefreshBtn>
                        ) : null}
                        <Btn
                          type="button"
                          onClick={() => handleRequestIssuance(i)}
                          disabled={busy || st === 'pending' || st === 'authenticated'}
                        >
                          {st === 'rejected' ? t('activities.issueResend') : t('activities.confirmIssue')}
                        </Btn>
                        {iss?.merchantNote ? <span className="note">{iss.merchantNote}</span> : null}
                      </IssueRow>
                    );
                  })() : null}
                  <RemoveLink onClick={() => removeRow('rewards', i)}>{t('activities.removeReward')}</RemoveLink>
                </RepeatItem>
              ))}
              <Btn type="button" onClick={() => addRow('rewards', BLANK_REWARD)}>{t('activities.addReward')}</Btn>
            </Section>
          </>
        )
      ) : null}

      {/* Action bar */}
      {!published ? (
        <BtnRow>
          {editingPublished ? (
            <>
              <Btn $primary type="button" onClick={handleUpdatePublished} disabled={busy}>
                {busy ? t('common.processing') : t('activities.updatePublished')}
              </Btn>
              <Btn type="button" onClick={() => {
                setEditingPublished(false);
                clearDraftCache();
                void load();
              }} disabled={busy}>
                {t('common.cancel')}
              </Btn>
            </>
          ) : (
            <>
              <Btn $primary type="button" onClick={handleCreate} disabled={busy}>
                {busy ? t('common.processing') : t('activities.genRules')}
              </Btn>
              <Btn type="button" onClick={handleSaveDraft} disabled={busy}>{t('common.saveDraft')}</Btn>
              <BtnDanger type="button" onClick={handleDelete} disabled={busy}>{t('common.delete')}</BtnDanger>
            </>
          )}
        </BtnRow>
      ) : null}
    </>
  );
}
